import { useState, useMemo, useRef, useEffect } from "react";
import { buildImageSrcSet } from "@shared/image-srcset";
import { createPortal } from "react-dom";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { usePrefetchRoute } from "@/hooks/use-prefetch-route";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useStudioConfig } from "@/hooks/use-studio-config";
import {
  ArrowLeft, ArrowRight, Calendar, CalendarIcon, Clock, ChevronLeft, ChevronRight, ChevronDown, CheckCircle,
  User, Mail, Phone, MessageSquare, Sparkles, DollarSign,
  Info, RotateCcw, AlertCircle, ImageIcon, Upload, X, ClipboardList, Bell, Link2,
} from "lucide-react";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import { useSiteConfig } from "@/lib/SiteConfigContext";
import { getAllArtists, fetchArtistsFromApi, type ArtistWithPortfolio } from "@/lib/artists";
import { TipGuide } from "@/components/TipGuide";
import BodyMapStep from "@/components/BodyMapStep";
import { useToast } from "@/hooks/use-toast";
import { getAttribution } from "@/hooks/useAttribution";
import { getEstimateVariant, ESTIMATE_AB_TEST_KEY, ALL_ESTIMATE_VARIANTS, PRICE_RANGE_BUCKETS, type EstimateVariant } from "@shared/ab-variant";
import { apiFetch, ApiFetchError } from "@/lib/apiFetch";
import { parseRateLimit } from "@/lib/rateLimit";
import { RateLimitError, TokenExpiredError } from "@/lib/queryClient";
import { bookedTimesForDateContract, bookingCreateContract, type BookingCreateResponse } from "@shared/contracts";
import {
  SIZES as _SIZES, COMPLEXITIES as _COMPLEXITIES, PLACEMENTS as _PLACEMENTS, COLOR_OPTIONS as _COLOR_OPTIONS,
  ARTIST_RATES as _ARTIST_RATES, DEFAULT_RATE as _DEFAULT_RATE,
  STEP_ICON_MAP, STEP_COLOR_MAP,
  ESTIMATE_SUB_STEPS, ESTIMATE_STEP_INFO,
  getEstimateOptions, calculateEstimate,
  type EstimateSubStep,
} from "@/lib/estimateData";
import {
  JEWELRY_MATERIALS, JEWELRY_GAUGES, JEWELRY_STYLES,
  calculatePiercingEstimate,
} from "@shared/piercing-estimate-config";
import { generateTimeSlots } from "@/lib/utils";
import { useFeatureFlags } from "@/lib/FeatureFlagContext";
import { FeatureFlagZone } from "@/components/FeatureFlagZone";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { SERVICE_TYPE_CONFIG, getServiceTypeConfig, getMainSteps, getAvailableServiceTypes, isStepVisible, getStepCopy, type ServiceTypeName as _ServiceTypeName } from "@/lib/serviceTypeConfig";
import { captureException } from "@/lib/sentry";
import { JsonLd } from "@/components/JsonLd";
import AiSketchGenerator from "@/components/AiSketchGenerator";

const BASE_SERVICE_TYPES = [
  "Custom Tattoo",
  "Flash Design",
  "Cover-Up",
  "Touch-Up",
  "Consultation Only",
];

function formatTimePref(time: string): string {
  return time;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function parseTimeSlotHour(t: string): number {
  const m = t.match(/^(\d+)(am|pm)$/i);
  if (!m) return 12;
  let h = parseInt(m[1], 10);
  const period = m[2].toLowerCase();
  if (period === "pm" && h !== 12) h += 12;
  if (period === "am" && h === 12) h = 0;
  return h;
}

function formatTimeSlot(t: string): string {
  const m = t.match(/^(\d+)(am|pm)$/i);
  if (!m) return t;
  const h = parseInt(m[1], 10);
  const period = m[2].toUpperCase();
  return `${h}:00 ${period}`;
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

const DRAFT_KEY = "booking_draft_v1";
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const DRAFT_SERVER_TOKEN_KEY = "booking_draft_server_token_v1";

function generateDraftToken(): string {
  const arr = new Uint8Array(24);
  crypto.getRandomValues(arr);
  return Array.from(arr).map((b) => b.toString(16).padStart(2, "0")).join("");
}

interface BookingDraft {
  v: 1;
  savedAt: number;
  step: string;
  selectedArtist: string;
  selectedDate: string;
  selectedTime: string;
  serviceCategory: string;
  form: { name: string; email: string; phone: string; serviceType: string; description: string; notes: string; subscribeToNewsletter: boolean };
  estSize: string | null;
  estComplexity: string | null;
  estPlacement: string | null;
  estColor: string | null;
  estSubStep: number;
  estShowResults: boolean;
  estimateStatus: "completed" | null;
  bodyMap: { x: number; y: number; view: "front" | "back" | "left" | "right" | "head-front" | "head-left" | "head-right"; radius: number; gender?: "male" | "female"; sizeInches?: string; placementNotes?: string } | null;
}

function timeSince(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days !== 1 ? "s" : ""} ago`;
}

// ---------- Variant D: post-submission estimate mini-flow ----------
function VariantDPostEstimate({
  bookingId,
  selfEstimateToken,
  onDone,
}: {
  bookingId: number;
  selfEstimateToken: string;
  onDone: () => void;
}) {
  const [subStep, setSubStep] = useState(0);
  const [estSize, setEstSize] = useState<string | null>(null);
  const [estComplexity, setEstComplexity] = useState<string | null>(null);
  const [estPlacement, setEstPlacement] = useState<string | null>(null);
  const [estColor, setEstColor] = useState<string | null>(null);
  const [animating, setAnimating] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<{ low: number; high: number; hours: number } | null>(null);

  const steps = ESTIMATE_SUB_STEPS;

  const getValue = (s: EstimateSubStep) => {
    if (s === "size") return estSize;
    if (s === "complexity") return estComplexity;
    if (s === "placement") return estPlacement;
    if (s === "color") return estColor;
    return null;
  };

  const handlePick = (step: EstimateSubStep, value: string) => {
    if (animating) return;
    setAnimating(value);
    if (step === "size") setEstSize(value);
    else if (step === "complexity") setEstComplexity(value);
    else if (step === "placement") setEstPlacement(value);
    else if (step === "color") setEstColor(value);

    setTimeout(async () => {
      setAnimating(null);
      if (subStep < steps.length - 1) {
        setSubStep(subStep + 1);
      } else {
        // Last step — compute and PATCH the booking
        const finalSize = step === "size" ? value : estSize;
        const finalComplexity = step === "complexity" ? value : estComplexity;
        const finalPlacement = step === "placement" ? value : estPlacement;
        const finalColor = step === "color" ? value : estColor;
        const calc = calculateEstimate(finalSize ?? "", finalComplexity ?? "", finalPlacement ?? "", finalColor ?? "", null);
        if (calc) {
          setEstimate(calc);
          setSubmitting(true);
          setSaveError(null);
          try {
            const patchRes = await fetch(`/api/bookings/${bookingId}/self-estimate`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                selfEstimateToken,
                tattooSize: finalSize,
                complexity: finalComplexity,
                placement: finalPlacement,
                color: finalColor,
                estimatedCostMin: calc.low,
                estimatedCostMax: calc.high,
                estimatedHours: calc.hours,
                estimateStatus: "completed",
              }),
            });
            if (!patchRes.ok) {
              const errBody = await patchRes.json().catch(() => ({ error: "Failed to save estimate" })) as { error?: string };
              setSaveError(errBody.error || "Failed to save estimate");
              setSubmitting(false);
              return;
            }
            setSubmitting(false);
            setSubmitted(true);
          } catch (_err) {
            setSaveError("Failed to save estimate — please contact the studio.");
            setSubmitting(false);
          }
        }
      }
    }, 380);
  };

  if (submitted && estimate) {
    return (
      <div className="rounded-xl border border-white/10 bg-black/60 p-6 max-w-md mx-auto text-center">
        <CheckCircle size={40} className="text-accent mx-auto mb-3" />
        <h3 className="font-heading text-white text-lg mb-1">Your Estimate</h3>
        <p className="text-white/60 text-xs mb-4">
          Saved to your booking — your artist will discuss exact pricing during consultation.
        </p>
        <div className="flex items-center justify-center gap-4 mb-4">
          <div className="text-center">
            <div className="text-2xl font-heading text-white">${estimate.low}–${estimate.high}</div>
            <div className="text-xs text-white/50">estimated range</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-heading text-white">{estimate.hours}h</div>
            <div className="text-xs text-white/50">estimated time</div>
          </div>
        </div>
        <button
          onClick={onDone}
          className="text-xs text-white/50 hover:text-white/80 transition-colors"
          data-testid="button-est-d-done"
        >
          Dismiss
        </button>
      </div>
    );
  }

  const currentSubStep = steps[subStep];
  const stepInfo = ESTIMATE_STEP_INFO[currentSubStep] ?? { title: "", subtitle: "" };
  const options = getEstimateOptions(currentSubStep);
  const currentValue = getValue(currentSubStep);

  return (
    <div className="rounded-xl border border-white/10 bg-black/60 p-6 max-w-md mx-auto" data-testid="variant-d-estimate">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <DollarSign size={14} className="text-accent shrink-0" />
          <span className="font-heading text-sm text-white">Quick Price Estimate</span>
        </div>
        <span className="text-xs text-white/40 font-subheading uppercase tracking-widest">
          Q{subStep + 1} of {steps.length}
        </span>
      </div>
      <h3 className="text-lg font-heading text-white mb-1" data-testid="text-est-d-question">{stepInfo.title}</h3>
      <p className="text-xs text-white/50 mb-5">{stepInfo.subtitle}</p>
      <div className="space-y-2">
        {options.map((option: { id: string; label: string; description?: string }) => {
          const isAnimating = animating === option.id;
          const isSelected = currentValue === option.id && !animating;
          return (
            <button
              key={option.id}
              onClick={() => handlePick(currentSubStep, option.id)}
              disabled={!!animating}
              className={`w-full text-left border rounded-lg p-4 transition-all group bg-black/60 ${
                isAnimating
                  ? "border-accent bg-accent/20 text-white scale-[0.98]"
                  : isSelected
                  ? "border-accent/40 bg-accent/10 text-white"
                  : animating
                  ? "border-white/5 text-white/40 opacity-60"
                  : "border-white/10 text-white/80 hover:text-white hover:border-accent/50 hover:bg-accent/5 active:bg-accent/20 active:border-accent active:text-white active:scale-[0.98]"
              }`}
              data-testid={`button-est-d-${option.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-subheading font-semibold text-sm block">{option.label}</span>
                  {option.description && <span className="text-xs text-white/60 mt-0.5 block">{option.description}</span>}
                </div>
                {isAnimating ? (
                  <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "#8b5cf6" }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                ) : (
                  <ArrowRight size={16} className="text-muted-foreground group-hover:text-accent transition-colors opacity-0 group-hover:opacity-100 shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>
      {subStep > 0 && (
        <button
          onClick={() => setSubStep(subStep - 1)}
          className="mt-4 inline-flex items-center gap-1 text-xs text-white/40 hover:text-white/70 transition-colors"
          data-testid="button-est-d-back"
        >
          <ArrowLeft size={12} /> Back
        </button>
      )}
      {submitting && (
        <p className="text-xs text-white/50 mt-3 text-center animate-pulse">Saving estimate…</p>
      )}
      {saveError && (
        <p className="text-xs text-red-400/80 mt-3 text-center" data-testid="text-est-d-error">{saveError}</p>
      )}
    </div>
  );
}

interface BookingCalendarProps {
  mode?: "public" | "admin" | "walkin" | "artist-portal";
  adminToken?: string;
  walkinToken?: string;
  artistPortalToken?: string;
  artistPortalSlug?: string;
}

export default function BookingCalendar({ mode = "public", adminToken, walkinToken, artistPortalToken, artistPortalSlug }: BookingCalendarProps = {}) {
  const { t } = useTranslation("booking");
  const isAdmin = mode === "admin";
  const isWalkin = mode === "walkin";
  const isArtistPortal = mode === "artist-portal";
  const isPublic = mode === "public";
  const { config: studio } = useStudioConfig();
  const { config } = useSiteConfig();

  const prefetchAftercare = usePrefetchRoute(
    "aftercare",
    () => import("@/pages/Aftercare"),
    8000,
  );
  const prefetchHealingTracker = usePrefetchRoute(
    "healing-tracker",
    () => import("@/pages/HealingTracker"),
    10000,
  );
  const { isLoaded: flagsLoaded, isFlagEnabled } = useFeatureFlags();
  const availableServiceTypes = getAvailableServiceTypes(isFlagEnabled);
  const SERVICE_TYPES = availableServiceTypes.flatMap((cfg) => cfg.formServiceTypes);

  const timeSlotGroups = useMemo(() => {
    const start = studio?.bookingSlotStartHour ?? 12;
    const end = studio?.bookingSlotEndHour ?? 18;
    const interval = studio?.bookingSlotIntervalMinutes ?? 30;
    const slots = generateTimeSlots(start, end, interval);
    const morning: string[] = [];
    const afternoon: string[] = [];
    const evening: string[] = [];
    for (const slot of slots) {
      const isPM = slot.includes("PM");
      const rawHour = parseInt(slot.split(":")[0], 10);
      const h = isPM && rawHour !== 12 ? rawHour + 12 : (!isPM && rawHour === 12 ? 0 : rawHour);
      if (h < 12) morning.push(slot);
      else if (h < 16) afternoon.push(slot);
      else evening.push(slot);
    }
    return [
      ...(morning.length   ? [{ group: "Morning",   icon: "☀️",  times: morning }]   : []),
      ...(afternoon.length ? [{ group: "Afternoon", icon: "🌤️", times: afternoon }] : []),
      ...(evening.length   ? [{ group: "Evening",   icon: "🌙",  times: evening }]   : []),
    ];
  }, [studio?.bookingSlotStartHour, studio?.bookingSlotEndHour, studio?.bookingSlotIntervalMinutes]);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: apiArtists } = useQuery<ArtistWithPortfolio[]>({
    queryKey: ["artists"],
    queryFn: fetchArtistsFromApi,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const artists = getAllArtists(apiArtists);

  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const urlParams = new URLSearchParams(window.location.search);
  const urlArtist = urlParams.get("artist") || "";
  const urlEstimate = parseInt(urlParams.get("estimate") || "0", 10) || 0;
  const urlEstSize = urlParams.get("estSize") || null;
  const urlEstComplexity = urlParams.get("estComplexity") || null;
  const urlEstPlacement = urlParams.get("estPlacement") || null;
  const urlEstColor = urlParams.get("estColor") || null;
  const urlFlash = urlParams.get("flash") || "";
  const urlFlashArtist = urlParams.get("flashArtist") || "";
  const urlFlashId = urlParams.get("flashId") || "";
  const urlFlashPriceRaw = urlParams.get("flashPrice") || "";
  const urlFlashPrice: number | null = (() => {
    const n = parseFloat(urlFlashPriceRaw);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const urlService = urlParams.get("service") || "";
  const urlStyle = urlParams.get("style") || "";
  const urlResumeToken = urlParams.get("resume") || "";
  const urlOccasion = urlParams.get("occasion") || "";
  const urlLabel = urlParams.get("label") || "";
  const urlRef = urlParams.get("ref") || "";
  const urlDropId = urlParams.get("dropId") || "";
  const urlClaimToken = urlParams.get("claimToken") || "";
  const validArtist = artists.some((a) => a.slug === urlArtist) ? urlArtist : (artists.some((a) => a.slug === urlFlashArtist) ? urlFlashArtist : "");
  const isFlashBooking = !!urlFlash;
  // Flash Day mode: ?occasion=flash — a general flash event deeplink with no specific design pre-selected.
  // Skips service-type and goes straight to artist/calendar; surfaces Flash Day messaging.
  const isFlashDay = urlOccasion === "flash" && !isFlashBooking;
  const isSoftBooking = isFlagEnabled("soft_booking_availability") && !isArtistPortal && !isWalkin && !isAdmin && !isFlashBooking && !isFlashDay;
  const isPiercingDeepLink = urlService === "piercing" && !isFlashBooking && !isFlashDay;
  // Studio Work deep-link: ?style=<serviceType> — pre-fills service type from a studio work card.
  // Skips the service-type step and jumps straight to artist selection.
  const isStyleDeepLink = !!urlStyle && !isFlashBooking && !isFlashDay && !isPiercingDeepLink;
  const urlStyleCategory: "tattoo" | "piercing" | "" = isStyleDeepLink
    ? (urlStyle.toLowerCase().includes("piercing") ? "piercing" : "tattoo")
    : "";

  const devDone = import.meta.env.DEV && urlParams.get("dev_done") === "1";
  const devRfToken = import.meta.env.DEV ? (urlParams.get("dev_rfToken") || null) : null;
  const devRfOnFile = import.meta.env.DEV && urlParams.get("dev_rfOnFile") === "1";
  const devDate = import.meta.env.DEV ? (urlParams.get("dev_date") || "") : "";
  const devTime = import.meta.env.DEV ? (urlParams.get("dev_time") || "") : "";

  // Dev widget — activated by ?devtools=1 (persists in localStorage) or by DEV env.
  // Deactivate with ?devtools=0. Safe to leave in production: no data is exposed.
  const [showDevWidget] = useState<boolean>(() => {
    if (urlParams.get("devtools") === "1") { localStorage.setItem("booking_devtools", "1"); return true; }
    if (urlParams.get("devtools") === "0") { localStorage.removeItem("booking_devtools"); return false; }
    return import.meta.env.DEV || localStorage.getItem("booking_devtools") === "1";
  });
  const [devWidgetOpen, setDevWidgetOpen] = useState<boolean>(false);
  const [devVariantOverride, setDevVariantOverride] = useState<EstimateVariant | null>(() => {
    const stored = localStorage.getItem("booking_estimate_variant") as EstimateVariant | null;
    return stored && (["A","B","C","D"] as EstimateVariant[]).includes(stored) ? stored : null;
  });

  const [turnstileToken, setTurnstileToken] = useState("");

  const [selectedArtist, setSelectedArtist] = useState<string>(isArtistPortal ? (artistPortalSlug ?? validArtist) : validArtist);
  const [selectedDate, setSelectedDate] = useState<string>(devDone ? devDate : "");
  const [selectedTime, setSelectedTime] = useState<string>(devDone ? devTime : "");
  const [availabilityDays, setAvailabilityDays] = useState<string[]>([]);
  const [availabilityTimes, setAvailabilityTimes] = useState<string[]>([]);
  const [highlightDay, _setHighlightDay] = useState<string | null>(null);
  // Service-type is always the first step unless it's a flash booking (which
  // pre-sets the service type) or a dev-done preview.
  const [step, setStep] = useState<"service-type" | "artist" | "flash-info" | "calendar" | "time" | "availability" | "estimate" | "body-map" | "images" | "form" | "notes" | "duplicate-check" | "limit-reached" | "walkin-warnings" | "done">(devDone ? "done" : (isFlashBooking ? (validArtist ? "flash-info" : "artist") : (isFlashDay ? (validArtist ? "calendar" : "artist") : (isPiercingDeepLink ? "artist" : (isStyleDeepLink ? "artist" : "service-type")))));
  const [returnToStep, setReturnToStep] = useState<"body-map" | "notes" | "form" | null>(null);
  const [showImagesSkipConfirm, setShowImagesSkipConfirm] = useState(false);
  const [flashWhatIsOpen, setFlashWhatIsOpen] = useState(false);
  const [sendClientEmail, setSendClientEmail] = useState(true);
  const [walkinDepositAmount, setWalkinDepositAmount] = useState("");
  const [artistPortalConfirmMode, setArtistPortalConfirmMode] = useState<"confirmed" | "pending">("confirmed");
  const [walkinWarnings, setWalkinWarnings] = useState<{ duplicate?: string; overbooking?: string } | null>(null);
  const urlStyleLabel = isStyleDeepLink ? urlStyle.charAt(0).toUpperCase() + urlStyle.slice(1) : "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", serviceType: isFlashBooking ? "Flash Design" : (isFlashDay ? "Flash Design" : (isPiercingDeepLink ? "Piercing" : (isStyleDeepLink ? urlStyleLabel : ""))), description: isFlashBooking ? `Flash design: ${urlFlash}${urlFlashId ? ` (ID: ${urlFlashId})` : ""}` : (isFlashDay && urlLabel ? `Flash Day${urlLabel ? `: ${urlLabel}` : ""}` : ""), notes: "", subscribeToNewsletter: false });
  const [bookingEmailTouched, setBookingEmailTouched] = useState(false);

  const [estSize, setEstSize] = useState<string | null>(urlEstSize);
  const [estComplexity, setEstComplexity] = useState<string | null>(urlEstComplexity);
  const [estPlacement, setEstPlacement] = useState<string | null>(urlEstPlacement);
  const [estColor, setEstColor] = useState<string | null>(urlEstColor);
  const [estSubStep, setEstSubStep] = useState(0);
  const [estAnimating, setEstAnimating] = useState<string | null>(null);
  const [estShowResults, setEstShowResults] = useState(false);
  const [estResetKey, setEstResetKey] = useState(0);
  const [estimateStatus, setEstimateStatus] = useState<"completed" | null>(
    urlEstSize && urlEstComplexity && urlEstPlacement && urlEstColor ? "completed" : null
  );
  // Variant C: simplified single price-range pick
  const [variantCBucket, setVariantCBucket] = useState<string | null>(null);
  const [variantCAnimating, setVariantCAnimating] = useState<string | null>(null);
  // Variant D: post-submission estimate state
  const [showVariantDPostEstimate, setShowVariantDPostEstimate] = useState(false);
  const [variantDSubmittedBookingId, setVariantDSubmittedBookingId] = useState<number | null>(null);
  const [variantDSelfEstimateToken, setVariantDSelfEstimateToken] = useState<string | null>(null);
  // Piercing estimator + form state
  const [piercingType, setPiercingType] = useState<string>("");
  const [piercingMaterial, setPiercingMaterial] = useState<string>("implant_titanium");
  const [piercingGauge, setPiercingGauge] = useState<string>("ask");
  const [piercingStyle, setPiercingStyle] = useState<string>("ask");
  const [piercingCount, setPiercingCount] = useState<number>(1);
  const [bodyMap, setBodyMap] = useState<{ x: number; y: number; view: "front" | "back" | "left" | "right" | "head-front" | "head-left" | "head-right"; radius: number; gender?: "male" | "female"; sizeInches?: string; placementNotes?: string } | null>(null);
  const [resumeCandidate, setResumeCandidate] = useState<BookingDraft | null>(null);
  const [bookingStarted, setBookingStarted] = useState(() => isAdmin || isWalkin || isArtistPortal || isFlashBooking || isFlashDay || isPiercingDeepLink || isStyleDeepLink || devDone);
  const draftReadyRef = useRef(false);
  // Task #977 — Captures the draft token only when the visitor entered via a
  // /booking?resume=<token> recovery email link. Forwarded to POST /api/bookings
  // so admins can see which bookings came from the recovery flow.
  const recoveredFromDraftTokenRef = useRef<string | null>(null);
  const draftServerTokenRef = useRef<string>(
    (() => {
      const existing = sessionStorage.getItem(DRAFT_SERVER_TOKEN_KEY);
      if (existing) return existing;
      const t = generateDraftToken();
      sessionStorage.setItem(DRAFT_SERVER_TOKEN_KEY, t);
      return t;
    })()
  );
  const resumeCardControls = useAnimation();
  const [resumeOutsideHint, setResumeOutsideHint] = useState(false);
  const resumeOutsideHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [exitEmail, setExitEmail] = useState("");
  const [exitEmailStatus, setExitEmailStatus] = useState<"idle" | "submitting">("idle");
  const [exitEmailTouched, setExitEmailTouched] = useState(false);
  const closeExitIntent = () => {
    setShowExitIntent(false);
    setExitEmail("");
    setExitEmailStatus("idle");
  };
  const pendingNavRef = useRef<(() => void) | null>(null);
  const bypassGuardRef = useRef(false);
  const exitIntentShownRef = useRef(false);

  // Hide the step-progress banner whenever the page footer scrolls into view
  useEffect(() => {
    if (!isPublic) return;
    const footer = document.querySelector("footer[role='contentinfo']");
    if (!footer) return;
    const observer = new IntersectionObserver(
      ([entry]) => setFooterVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(footer);
    return () => observer.disconnect();
  }, [isPublic]);

  // Lock body scroll and run entrance animation while resume prompt is showing
  useEffect(() => {
    if (!resumeCandidate) return;
    // iOS-safe scroll lock: save position, fix body in place so it can't scroll under the overlay
    const scrollY = window.scrollY;
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    // Entrance animation via controls
    resumeCardControls.start({ opacity: 1, scale: 1, y: 0, transition: { duration: 0.25, delay: 0.05 } });
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;
      window.scrollTo(0, scrollY);
    };
  }, [!!resumeCandidate]);

  const handleResumeBackdropClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    resumeCardControls.start({
      x: [0, -10, 10, -7, 7, -4, 4, 0],
      transition: { duration: 0.45, ease: "easeInOut" },
    });
    if (resumeOutsideHintTimerRef.current) clearTimeout(resumeOutsideHintTimerRef.current);
    setResumeOutsideHint(true);
    resumeOutsideHintTimerRef.current = setTimeout(() => setResumeOutsideHint(false), 3000);
  };
  const [artistAnimating, setArtistAnimating] = useState<string | null>(null);
  const [serviceCategoryAnimating, setServiceCategoryAnimating] = useState<string | null>(null);
  const [tappedBookedOutSlug, setTappedBookedOutSlug] = useState<string | null>(null);
  const [serviceCategory, setServiceCategory] = useState<"tattoo" | "piercing" | "">(isFlashBooking || isFlashDay ? "tattoo" : (isPiercingDeepLink ? "piercing" : (isStyleDeepLink ? urlStyleCategory : "")));
  const [dateAnimating, setDateAnimating] = useState<number | null>(null);
  const [timeAnimating, setTimeAnimating] = useState<string | null>(null);
  const [duplicateBooking, setDuplicateBooking] = useState<{ referenceCode: string; date: string; time: string; artistName: string } | null>(null);
  const [limitInfo, setLimitInfo] = useState<{ submitted: number; limit: number; studioEmail: string | null; studioPhone: string | null; studioName: string } | null>(null);
  const pendingSubmitRef = useRef<Record<string, any> | null>(null);
  const idempotencyKeyRef = useRef<string | null>(null);

  const funnelSessionIdRef = useRef<string | null>(null);
  const funnelReferrerRef = useRef<string | null>(null);
  const [estimateVariant, setEstimateVariant] = useState<EstimateVariant>("A");
  const stepEnteredAtRef = useRef<number>(Date.now());
  const prevStepNameRef = useRef<string | null>(null);
  const trackedStepsRef = useRef(new Set<string>());
  // Annotation events (ab:*, estimate-d-entry, etc.) must NOT be counted in trackedStepsRef
  // because the first-step referrer logic uses trackedStepsRef.size === 1 to detect the first real step.
  const trackedAnnotationsRef = useRef(new Set<string>());
  const stepContentRef = useRef<HTMLDivElement>(null);
  const skipNextScrollRef = useRef(false);
  const calendarSyncedRef = useRef<string | null>(null);

  // True once the public user has chosen a service type and hasn't finished yet.
  // All three exit guards only activate while this is true.
  const hasProgress = isPublic && bookingStarted && serviceCategory !== "" && step !== "done";

  // Guard 1: beforeunload — native "Leave site?" dialog on refresh / tab-close / typed URL
  useEffect(() => {
    if (!hasProgress) return;
    const handler = (e: BeforeUnloadEvent) => {
      if (bypassGuardRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasProgress]);

  // Guard 2: pushState intercept — catches Wouter <Link> clicks and navigate() calls
  useEffect(() => {
    if (!hasProgress) return;
    const orig = window.history.pushState.bind(window.history);
    window.history.pushState = (...args: Parameters<typeof window.history.pushState>) => {
      if (bypassGuardRef.current) { orig(...args); return; }
      const target = String(args[2] ?? "");
      const curr = window.location.pathname;
      if (target && target !== curr && !target.startsWith(curr + "?") && !target.startsWith(curr + "#")) {
        pendingNavRef.current = () => { bypassGuardRef.current = true; orig(...args); };
        setShowLeaveModal(true);
        return;
      }
      orig(...args);
    };
    return () => { window.history.pushState = orig; };
  }, [hasProgress]);

  // Deep-link guard: if ?service=piercing was used but piercings_enabled is off,
  // reset to the normal service-type step once feature flags finish loading.
  useEffect(() => {
    if (!flagsLoaded) return;
    if (urlService === "piercing" && !isFlagEnabled("piercings_enabled")) {
      setServiceCategory("");
      setStep("service-type");
      setForm((prev) => ({ ...prev, serviceType: "" }));
      setBookingStarted(false);
    }
  }, [flagsLoaded]);

  // Auto-skip the service-type step when only one service type is available.
  // Runs once after feature flags have loaded. Only applies when we are still
  // sitting on the service-type step (i.e. no deep-link pre-selection and no
  // flash booking already resolved the step differently).
  useEffect(() => {
    if (!flagsLoaded) return;
    if (step !== "service-type") return;
    if (isFlashBooking) return;
    const available = getAvailableServiceTypes(isFlagEnabled);
    if (available.length !== 1) return;
    const only = available[0];
    setServiceCategory(only.slug);
    const defaultSvc = SERVICE_TYPE_CONFIG[only.slug].formServiceTypes[0] ?? "";
    setForm((prev) => ({ ...prev, serviceType: defaultSvc }));
    if (isArtistPortal) {
      setStep("calendar");
    } else {
      let artistStillValid = false;
      if (selectedArtist) {
        const preselected = artists.find((a) => a.slug === selectedArtist);
        const svcTypes = preselected?.serviceTypes ?? ["Tattoo"];
        const incompatible = !svcTypes.includes(SERVICE_TYPE_CONFIG[only.slug].displayName);
        if (!incompatible) artistStillValid = true;
      }
      setStep(artistStillValid ? (isSoftBooking ? "availability" : "calendar") : "artist");
    }
  }, [flagsLoaded]);

  // Guard 3: popstate — browser back / forward button
  useEffect(() => {
    if (!hasProgress) return;
    const handler = () => {
      if (bypassGuardRef.current) return;
      window.history.pushState(null, "", window.location.pathname);
      pendingNavRef.current = () => { bypassGuardRef.current = true; window.history.go(-2); };
      setShowLeaveModal(true);
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [hasProgress]);

  // Guard 4: exit-intent — (a) mouse exits top of viewport OR (b) 2 min idle
  useEffect(() => {
    if (!hasProgress) return;

    const fire = () => {
      if (exitIntentShownRef.current) return;
      exitIntentShownRef.current = true;
      setShowExitIntent(true);
    };

    // (a) mouse exits from top of viewport
    const mouseHandler = (e: MouseEvent) => {
      if (e.clientY <= 5) fire();
    };
    document.addEventListener("mouseleave", mouseHandler);

    // (b) idle timer — fire after 2 minutes of no mouse/key/scroll/touch activity
    const IDLE_MS = 2 * 60 * 1000;
    let idleTimer: ReturnType<typeof setTimeout> = setTimeout(fire, IDLE_MS);
    const resetIdle = () => {
      if (exitIntentShownRef.current) return;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(fire, IDLE_MS);
    };
    const activityEvents = ["mousemove", "keydown", "scroll", "touchstart", "click"] as const;
    activityEvents.forEach(ev => document.addEventListener(ev, resetIdle, { passive: true }));

    return () => {
      document.removeEventListener("mouseleave", mouseHandler);
      clearTimeout(idleTimer);
      activityEvents.forEach(ev => document.removeEventListener(ev, resetIdle));
    };
  }, [hasProgress]);

  useEffect(() => {
    // Wait until feature flags have loaded so we don't assign before knowing the kill-switch state
    if (!flagsLoaded) return;

    let sessionId: string;
    const stored = localStorage.getItem("bookingFunnelSessionId");
    if (stored) {
      sessionId = stored;
      funnelSessionIdRef.current = stored;
    } else {
      sessionId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem("bookingFunnelSessionId", sessionId);
      funnelSessionIdRef.current = sessionId;
    }
    // Dev override: if the dev widget has pinned a variant, use it directly.
    const devOverride = localStorage.getItem("booking_estimate_variant") as EstimateVariant | null;
    if (devOverride && (["A","B","C","D"] as EstimateVariant[]).includes(devOverride)) {
      setEstimateVariant(devOverride);
      return;
    }
    // Assign estimate A/B variant — gated by master flag + per-variant flags.
    // When master is OFF → variant A (original flow) for everyone.
    // When master is ON → only distribute among the individually enabled variants.
    const abTestEnabled = isFlagEnabled("ab_estimate_enabled");
    const enabledVariants = abTestEnabled
      ? ALL_ESTIMATE_VARIANTS.filter(v => isFlagEnabled(`ab_estimate_variant_${v.toLowerCase()}`))
      : [];
    const variant = abTestEnabled
      ? getEstimateVariant(sessionId, ESTIMATE_AB_TEST_KEY, enabledVariants.length > 0 ? enabledVariants : ["A"])
      : "A";
    setEstimateVariant(variant);
    // Fire ab:estimate annotation if not yet tracked for this session.
    // Uses trackedAnnotationsRef (not trackedStepsRef) so the first-step referrer
    // logic (trackedStepsRef.size === 1) is not broken by this pre-step annotation.
    const abEventKey = `ab:estimate`;
    if (!trackedAnnotationsRef.current.has(abEventKey)) {
      trackedAnnotationsRef.current.add(abEventKey);
      fetch("/api/booking-funnel-event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, stepName: abEventKey, artistSlug: `variant:${variant}` }),
      }).catch(() => { /* intentionally silent: best-effort side-effect */ });
    }
    // Build referrer context: prefer document.referrer; fall back to utm_source/utm_medium
    // so paid/direct campaign traffic is captured even without an HTTP referrer header.
    const attr = getAttribution();
    let ref = attr.referrer || "";
    if (!ref && attr.utmSource) {
      // Synthesise a pseudo-referrer so the analytics table can group by source
      ref = attr.utmMedium ? `utm:${attr.utmSource}/${attr.utmMedium}` : `utm:${attr.utmSource}`;
    }
    if (ref && ref.length < 500) {
      funnelReferrerRef.current = ref;
    }
  }, [flagsLoaded]);
  useEffect(() => {
    if (isAdmin || isWalkin || isArtistPortal || isFlashBooking) {
      draftReadyRef.current = true;
      return;
    }
    // Server-side resume via email link (?resume=TOKEN)
    if (urlResumeToken) {
      fetch(`/api/booking-draft/resume/${urlResumeToken}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data?.draft?.state) {
            const state = data.draft.state as BookingDraft;
            // Adopt the resume token as our session token so saves continue
            draftServerTokenRef.current = urlResumeToken;
            sessionStorage.setItem(DRAFT_SERVER_TOKEN_KEY, urlResumeToken);
            // Tag the session as "came from recovery email" so the booking POST
            // can record the recovery attribution. Server validates the token
            // actually had a recovery email sent before tagging the booking.
            recoveredFromDraftTokenRef.current = urlResumeToken;
            // Hydrate form state
            if (state.selectedArtist) setSelectedArtist(state.selectedArtist);
            if (state.selectedDate) setSelectedDate(state.selectedDate);
            if (state.selectedTime) setSelectedTime(state.selectedTime);
            if (state.serviceCategory) setServiceCategory(state.serviceCategory as "tattoo" | "piercing" | "");
            if (state.form) setForm(state.form);
            if (state.estSize !== undefined) setEstSize(state.estSize);
            if (state.estComplexity !== undefined) setEstComplexity(state.estComplexity);
            if (state.estPlacement !== undefined) setEstPlacement(state.estPlacement);
            if (state.estColor !== undefined) setEstColor(state.estColor);
            if (state.estSubStep !== undefined) setEstSubStep(state.estSubStep);
            if (state.estShowResults !== undefined) setEstShowResults(state.estShowResults);
            if (state.estimateStatus !== undefined) setEstimateStatus(state.estimateStatus);
            if (state.bodyMap !== undefined) setBodyMap(state.bodyMap);
            setStep((state.step || "service-type") as Parameters<typeof setStep>[0]);
            setBookingStarted(true);
            // Remove the resume param from URL without reloading
            const url = new URL(window.location.href);
            url.searchParams.delete("resume");
            window.history.replaceState({}, "", url.toString());
          }
          draftReadyRef.current = true;
        })
        .catch(() => { draftReadyRef.current = true; });
      return;
    }
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) { draftReadyRef.current = true; return; }
      const draft = JSON.parse(raw) as BookingDraft;
      if (draft.v !== 1 || Date.now() - draft.savedAt > DRAFT_TTL_MS) {
        localStorage.removeItem(DRAFT_KEY);
        draftReadyRef.current = true;
        return;
      }
      if (!draft.selectedArtist && !draft.selectedDate && draft.step === "service-type") {
        draftReadyRef.current = true;
        return;
      }
      setResumeCandidate(draft);
    } catch {
      // intentionally silent
      draftReadyRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (step === "done") {
      prefetchAftercare();
      prefetchHealingTracker();
    }
  }, [step]);

  useEffect(() => {
    if (!draftReadyRef.current) return;
    if (isAdmin || isWalkin || isFlashBooking) return;
    if (step === "done") {
      localStorage.removeItem(DRAFT_KEY);
      sessionStorage.removeItem(DRAFT_SERVER_TOKEN_KEY);
      const token = draftServerTokenRef.current;
      if (token) {
        fetch(`/api/booking-draft/${token}/complete`, { method: "POST" }).catch(() => {});
      }
      return;
    }
    const draft: BookingDraft = {
      v: 1,
      savedAt: Date.now(),
      step,
      selectedArtist,
      selectedDate,
      selectedTime,
      serviceCategory,
      form,
      estSize,
      estComplexity,
      estPlacement,
      estColor,
      estSubStep,
      estShowResults,
      estimateStatus,
      bodyMap,
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    // Server-side draft persistence — only when email is present
    if (form.email) {
      const token = draftServerTokenRef.current;
      fetch("/api/booking-draft", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          email: form.email,
          artistSlug: selectedArtist || null,
          lastStep: step,
          state: draft,
        }),
      }).catch(() => {});
    }
  }, [step, selectedArtist, selectedDate, selectedTime, serviceCategory, form, estSize, estComplexity, estPlacement, estColor, estSubStep, estShowResults, estimateStatus, bodyMap]);

  const handleResumeDraft = () => {
    if (!resumeCandidate) return;
    const d = resumeCandidate;
    setSelectedArtist(d.selectedArtist);
    setSelectedDate(d.selectedDate);
    setSelectedTime(d.selectedTime);
    setServiceCategory(d.serviceCategory as "tattoo" | "piercing" | "");
    setForm(d.form);
    setEstSize(d.estSize);
    setEstComplexity(d.estComplexity);
    setEstPlacement(d.estPlacement);
    setEstColor(d.estColor);
    setEstSubStep(d.estSubStep);
    setEstShowResults(d.estShowResults);
    setEstimateStatus(d.estimateStatus);
    if (d.bodyMap) setBodyMap(d.bodyMap);
    setStep(d.step as Parameters<typeof setStep>[0]);
    setReturnToStep(null);
    setBookingStarted(true);
    setResumeCandidate(null);
    draftReadyRef.current = true;
    skipNextScrollRef.current = true; // don't auto-scroll on resume — user is already in the right place
    exitIntentShownRef.current = false; // reset so this session can see the exit-intent toast
    // Emit a return-session marker so analytics can track how many visitors resumed
    const sessionId = funnelSessionIdRef.current;
    if (sessionId && !trackedStepsRef.current.has("session_resumed")) {
      trackedStepsRef.current.add("session_resumed");
      fetch("/api/booking-funnel-event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, stepName: "session_resumed" }),
      }).catch(() => { /* intentionally silent: best-effort side-effect */ });
    }
  };

  const handleStartFresh = () => {
    localStorage.removeItem(DRAFT_KEY);
    setResumeCandidate(null);
    setReturnToStep(null);
    draftReadyRef.current = true;
  };

  useEffect(() => {
    if (!bookingStarted) return;
    if (skipNextScrollRef.current) { skipNextScrollRef.current = false; return; }
    const el = stepContentRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(id);
  }, [step]);

  useEffect(() => {
    const sessionId = funnelSessionIdRef.current;
    if (!sessionId) return;
    const now = Date.now();
    const prevStep = prevStepNameRef.current;
    const elapsed = now - stepEnteredAtRef.current;
    // Always advance timing state on every step transition — including back-navigation to
    // already-tracked steps — so elapsed times stay accurate regardless of navigation order.
    stepEnteredAtRef.current = now;
    prevStepNameRef.current = step;
    // Only emit a new server event the first time this step is reached in this session.
    if (trackedStepsRef.current.has(step)) return;
    trackedStepsRef.current.add(step);
    // Only pass referrer on the first-ever event for this session
    const isFirstEvent = trackedStepsRef.current.size === 1;
    const referrer = isFirstEvent ? (funnelReferrerRef.current ?? undefined) : undefined;
    // Include previous step's duration atomically — server updates the most-recent
    // event for prevStepName via subquery (race-safe; no async id tracking).
    fetch("/api/booking-funnel-event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId,
        stepName: step,
        artistSlug: selectedArtist || undefined,
        referrer,
        ...(prevStep && elapsed > 0 ? { prevStepName: prevStep, prevStepDurationMs: elapsed } : {}),
      }),
    }).catch(() => { /* intentionally silent: best-effort side-effect */ });
  }, [step]);

  const trackSubFunnelEvent = (subStepName: string) => {
    const sessionId = funnelSessionIdRef.current;
    if (!sessionId || trackedStepsRef.current.has(subStepName)) return;
    const now = Date.now();
    const prevStep = prevStepNameRef.current;
    const elapsed = now - stepEnteredAtRef.current;
    stepEnteredAtRef.current = now;
    trackedStepsRef.current.add(subStepName);
    prevStepNameRef.current = subStepName;
    fetch("/api/booking-funnel-event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId,
        stepName: subStepName,
        artistSlug: selectedArtist || undefined,
        ...(prevStep && elapsed > 0 ? { prevStepName: prevStep, prevStepDurationMs: elapsed } : {}),
      }),
    }).catch(() => { /* intentionally silent: best-effort side-effect */ });
  };

  useEffect(() => {
    if (step !== "estimate") return;
    const subStepName = `estimate:${ESTIMATE_SUB_STEPS[estSubStep]}`;
    trackSubFunnelEvent(subStepName);
  }, [step, estSubStep]);

  useEffect(() => {
    if (step === "estimate" && estShowResults) {
      trackSubFunnelEvent("estimate:results");
    }
  }, [step, estShowResults]);

  const [phoneError, setPhoneError] = useState<string>("");
  const [referenceImages, setReferenceImages] = useState<{ file: File; preview: string }[]>([]);
  const [referenceImageUrls, setReferenceImageUrls] = useState<string[]>([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [referenceLinks, setReferenceLinks] = useState<string>("");
  const [linkPreviews, setLinkPreviews] = useState<{ url: string; previewUrl: string | null; loading: boolean; error: boolean }[]>([]);
  const linkDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [submittedReleaseFormToken, setSubmittedReleaseFormToken] = useState<string | null>(devRfToken);
  const [submittedReleaseFormOnFile, setSubmittedReleaseFormOnFile] = useState(devRfOnFile);
  const [submittedDepositRequired, setSubmittedDepositRequired] = useState(false);
  const [submittedDepositAmount, setSubmittedDepositAmount] = useState<number | null>(null);
  const [submittedPaymentLink, setSubmittedPaymentLink] = useState<string | null>(null);
  const [submittedReferenceImageSignedUrls, setSubmittedReferenceImageSignedUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const MAX_REFERENCE_IMAGES = 10;

  const [showWaitlistForm, setShowWaitlistForm] = useState(false);
  const [waitlistTargetArtistSlug, setWaitlistTargetArtistSlug] = useState<string | null>(null);
  const [waitlistForm, setWaitlistForm] = useState({ name: "", email: "", phone: "", serviceType: "", notes: "", preferredMonth: "" });
  const [waitlistEmailTouched, setWaitlistEmailTouched] = useState(false);
  const [waitlistStatus, setWaitlistStatus] = useState<null | "submitting" | "done" | "alreadyOnList" | "error">(null);
  const [notesWiggle, setNotesWiggle] = useState(false);
  const [notesNotif, setNotesNotif] = useState(false);
  const notesWiggleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notesNotifTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasUrlEstimate = urlEstimate > 0;

  // ── Debounced inspiration-link preview ────────────────────────────────────
  useEffect(() => {
    if (linkDebounceRef.current) clearTimeout(linkDebounceRef.current);
    linkDebounceRef.current = setTimeout(async () => {
      const lines = referenceLinks.split("\n").map((l) => l.trim()).filter(Boolean);
      if (lines.length === 0) { setLinkPreviews([]); return; }
      setLinkPreviews(lines.map((url) => ({ url, previewUrl: null, loading: true, error: false })));
      for (let i = 0; i < lines.length; i++) {
        const url = lines[i];
        try {
          const resp = await fetch(`/api/booking-preview-link?url=${encodeURIComponent(url)}`);
          if (resp.ok) {
            const data = await resp.json() as { previewUrl?: string };
            setLinkPreviews((prev) => prev.map((p, idx) => idx === i ? { ...p, previewUrl: data.previewUrl ?? null, loading: false } : p));
          } else {
            setLinkPreviews((prev) => prev.map((p, idx) => idx === i ? { ...p, loading: false, error: true } : p));
          }
        } catch {
          setLinkPreviews((prev) => prev.map((p, idx) => idx === i ? { ...p, loading: false, error: true } : p));
        }
      }
    }, 800);
    return () => { if (linkDebounceRef.current) clearTimeout(linkDebounceRef.current); };
  }, [referenceLinks]);

  const estimate = useMemo(() => {
    if (hasUrlEstimate) {
      return {
        low: Math.round(urlEstimate * 0.8),
        high: Math.round(urlEstimate * 1.2),
        hours: 0,
        sessions: 0,
        mid: urlEstimate,
      };
    }
    if (!estSize || !estComplexity || !estPlacement || !estColor) return null;
    return calculateEstimate(estSize, estComplexity, estPlacement, estColor, selectedArtist, 0.5);
  }, [estSize, estComplexity, estPlacement, estColor, selectedArtist]);

  interface AvailabilityData {
    artistSlug: string;
    status: string;
    nextAvailableDate: string | null;
    minWeeksOut: number;
    note: string | null;
    blockedDates?: string[];
    recurringBlockedDays?: number[];
  }

  const { data: availabilityList = [] } = useQuery<AvailabilityData[]>({
    queryKey: ["/api/availability"],
    staleTime: 3 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const { data: depositReadiness } = useQuery<{ depositsRequired: boolean; requireDepositAtBooking: boolean; paymentConfigured: boolean; genericPaymentLink: string | null }>({
    queryKey: ["/api/studio/deposit-readiness"],
    enabled: isPublic,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const showDepositGate = isPublic && (depositReadiness?.depositsRequired ?? false) && !(depositReadiness?.paymentConfigured ?? true);

  const { data: piercingPricingData = [] } = useQuery<Array<{ id: number; piercingType: string; displayName: string; priceMin: number; priceMax: number; jewelryUpcharge: number }>>({
    queryKey: ["/api/piercing-pricing"],
    enabled: serviceCategory === "piercing",
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const piercingEstimate = useMemo(() => {
    if (!piercingType || piercingCount < 1) return null;
    return calculatePiercingEstimate(piercingType, piercingMaterial, piercingCount, piercingPricingData);
  }, [piercingType, piercingMaterial, piercingCount, piercingPricingData]);

  const { data: bookedTimesRaw = [], isFetching: bookedTimesFetching } = useQuery({
    queryKey: ["booked-times", selectedArtist, selectedDate],
    queryFn: () =>
      apiFetch(bookedTimesForDateContract, {
        params: { artistSlug: selectedArtist, date: selectedDate },
      }),
    enabled: !!selectedArtist && !!selectedDate,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const bookedTimesSet = useMemo(() => {
    const bookings = Array.isArray(bookedTimesRaw) ? bookedTimesRaw : [];
    const allSlots = timeSlotGroups.flatMap((g) => g.times);
    const blocked = new Set<string>();
    const parseMins = (t: string) => {
      const m = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (!m) return null;
      let h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      const ampm = m[3].toUpperCase();
      if (ampm === "PM" && h !== 12) h += 12;
      if (ampm === "AM" && h === 12) h = 0;
      return h * 60 + min;
    };
    for (const booking of bookings) {
      const startMins = parseMins(booking.time);
      if (startMins === null) { blocked.add(booking.time); continue; }
      const durationMins = Math.max(Math.ceil((booking.estimatedHours ?? 1) * 60), 30);
      const endMins = startMins + durationMins;
      for (const slot of allSlots) {
        const slotMins = parseMins(slot);
        if (slotMins !== null && slotMins >= startMins && slotMins < endMins) blocked.add(slot);
      }
    }
    return blocked;
  }, [bookedTimesRaw, timeSlotGroups]);

  const availableTimeSlotGroups = useMemo(
    () =>
      timeSlotGroups
        .map((group) => ({ ...group, times: group.times.filter((t) => !bookedTimesSet.has(t)) }))
        .filter((group) => group.times.length > 0),
    [timeSlotGroups, bookedTimesSet],
  );

  const getRelativeWeeks = (dateStr: string | null) => {
    if (!dateStr) return null;
    const now = new Date();
    const target = new Date(dateStr + "T00:00:00");
    const diffMs = target.getTime() - now.getTime();
    if (diffMs <= 0) return null;
    const weeks = Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));
    if (weeks < 1) return "less than a week";
    if (weeks === 1) return "~1 week";
    return `~${weeks} weeks`;
  };

  const availabilityMap = useMemo(() => {
    const map = new Map<string, AvailabilityData>();
    availabilityList.forEach((a) => map.set(a.artistSlug, a));
    return map;
  }, [availabilityList]);

  const selectedArtistAvailability = availabilityMap.get(selectedArtist);


  const bookingMutation = useMutation({
    mutationFn: async (data: typeof form & { artistSlug: string; date: string; time: string; tattooSize?: string | null; complexity?: string | null; placement?: string | null; color?: string | null; estimatedCostMin?: number | null; estimatedCostMax?: number | null; estimatedHours?: number | null; estimateStatus?: "completed" | null; confirmDuplicate?: boolean; sendClientEmail?: boolean; acknowledge?: boolean }): Promise<BookingCreateResponse & { existingBooking?: { referenceCode: string; date: string; time: string; artistSlug: string | null; artistName: string } }> => {
      // The public booking endpoint is wired through the contract layer so any
      // future drift between this page and the backend gets caught by
      // tests/contracts.test.ts. Admin / walkin / artist-portal variants still
      // use raw fetch; they are tracked as a follow-up migration.
      if (!isAdmin && !isWalkin && !isArtistPortal) {
        try {
          const iKey = idempotencyKeyRef.current;
          return await apiFetch(bookingCreateContract, {
            body: data,
            ...(iKey ? { headers: { "Idempotency-Key": iKey } } : {}),
          });
        } catch (err) {
          if (err instanceof ApiFetchError) {
            if (err.status === 429) throw err;
            const msg = (err.body as { error?: string } | undefined)?.error ?? err.message;
            throw new Error(msg || "Failed to book");
          }
          captureException(err);
          throw err;
        }
      }
      const endpoint = isAdmin
        ? "/api/admin/bookings"
        : isWalkin
        ? `/api/walkin/${walkinToken}`
        : `/api/artist-portal/${artistPortalToken}/bookings`;
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (isAdmin && adminToken) headers["x-admin-key"] = adminToken;
      if (idempotencyKeyRef.current) headers["Idempotency-Key"] = idempotencyKeyRef.current;
      // Propagate the smoke-test signal so the server can skip email side-effects
      // even when the email address doesn't match a known test pattern.
      if (typeof window !== "undefined" && (window as Window & { __SMOKE_TEST__?: boolean }).__SMOKE_TEST__) {
        headers["X-QA-Test"] = "true";
      }
      const res = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });
      if (res.status === 429) {
        const retryAfter = res.headers.get("retry-after");
        const seconds = retryAfter ? parseInt(retryAfter, 10) : null;
        throw new RateLimitError(Number.isFinite(seconds) ? seconds : null);
      }
      if (!res.ok) {
        const err = await res.json();
        if (res.status === 401) {
          throw new TokenExpiredError(err.error || "Session expired");
        }
        if (!err.slotConflict) {
          captureException(new Error(err.error || "Failed to book"));
        }
        throw new Error(err.error || "Failed to book");
      }
      return res.json();
    },
    onSuccess: (data) => {
      if (!isAdmin && !isWalkin && !isArtistPortal && data.limitReached) {
        setLimitInfo({ submitted: data.submitted ?? 0, limit: data.limit ?? 5, studioEmail: data.studioEmail ?? null, studioPhone: data.studioPhone ?? null, studioName: data.studioName ?? "the studio" });
        setStep("limit-reached");
        return;
      }
      if (isWalkin && data.requiresAcknowledgment) {
        setWalkinWarnings({ duplicate: data.duplicateWarning, overbooking: data.overbookingWarning });
        setStep("walkin-warnings");
        return;
      }
      if (!isAdmin && !isWalkin && !isArtistPortal && data.potentialDuplicate && data.existingBooking) {
        setDuplicateBooking(data.existingBooking);
        setStep("duplicate-check");
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["/api/bookings"] });
      setSubmittedReleaseFormToken(data.releaseFormToken ?? null);
      setSubmittedReleaseFormOnFile(data.releaseFormOnFile ?? false);
      setSubmittedDepositRequired(data.depositRequired ?? false);
      setSubmittedDepositAmount(typeof data.depositAmount === "number" ? data.depositAmount : null);
      setSubmittedPaymentLink(data.genericPaymentLink ?? null);
      setSubmittedReferenceImageSignedUrls(Array.isArray(data.referenceImageSignedUrls) ? data.referenceImageSignedUrls : []);
      // Variant D: store the booking ID + HMAC token so the optional post-submit teaser can securely PATCH
      if (estimateVariant === "D" && data.id && isPublic) {
        setVariantDSubmittedBookingId(data.id);
        setVariantDSelfEstimateToken(data.selfEstimateToken ?? null);
        // showVariantDPostEstimate stays false — user must explicitly opt in via the teaser card
      }
      setStep("done");
    },
    onError: (err: Error) => {
      if (err instanceof TokenExpiredError) {
        // Reset the idempotency key so the next attempt is treated as a fresh request
        // (the expired session means we cannot safely deduplicate against the previous key).
        idempotencyKeyRef.current = null;
        toast({ title: "Session Expired", description: "Your session has expired. Please try submitting again.", variant: "destructive" });
        return;
      }
      const rl = parseRateLimit(err);
      if (rl) {
        toast({
          title: "Slow down",
          description: rl.message,
          variant: "destructive",
          duration: rl.retryAfterSeconds ? Math.max(rl.retryAfterSeconds * 1000, 4000) : 6000,
        });
        return;
      }
      toast({ title: "Booking Failed", description: err.message, variant: "destructive" });
    },
  });

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const monthName = new Date(currentYear, currentMonth).toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); }
    else setCurrentMonth(currentMonth - 1);
  };

  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); }
    else setCurrentMonth(currentMonth + 1);
  };

  const selectArtist = (slug: string) => {
    setArtistAnimating(slug);
    setTimeout(() => {
      setArtistAnimating(null);
      setSelectedArtist(slug);
      setSelectedDate("");
      setSelectedTime("");
      setAvailabilityDays([]);
      setAvailabilityTimes([]);
      setStep(isFlashBooking ? "flash-info" : (isSoftBooking ? "availability" : "calendar"));
    }, 400);
  };

  const selectDate = (day: number) => {
    setDateAnimating(day);
    setTimeout(() => {
      setDateAnimating(null);
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      setSelectedDate(dateStr);
      if (returnToStep) {
        setStep(returnToStep);
        setReturnToStep(null);
      } else {
        setSelectedTime("");
        setStep(isSoftBooking ? "availability" : "time");
      }
    }, 400);
  };

  const selectTime = (time: string) => {
    setTimeAnimating(time);
    setTimeout(() => {
      setTimeAnimating(null);
      setSelectedTime(time);
      if (returnToStep) {
        setStep(returnToStep);
        setReturnToStep(null);
      } else {
        const showEstimate = isStepVisible(serviceCategory, "estimate");
        const skipEstimate = serviceCategory === "piercing"
          ? !showEstimate
          : (hasUrlEstimate || isFlashBooking || isFlashDay || estimateVariant === "D" || !showEstimate);
        if (skipEstimate) {
          setStep("body-map");
        } else {
          setEstSubStep(0);
          setEstShowResults(false);
          setEstResetKey(k => k + 1);
          setEstimateStatus("completed");
          setStep("estimate");
        }
      }
    }, 400);
  };

  const validatePhone = (value: string): string => {
    if (!value) return "";
    const digits = value.replace(/\D/g, "");
    if (digits.length > 0 && digits.length < 10) return "Please enter a complete 10-digit phone number.";
    return "";
  };

  const handleFormContinue = (e: React.FormEvent) => {
    e.preventDefault();
    const pErr = validatePhone(form.phone);
    if (pErr) { setPhoneError(pErr); return; }
    if (!form.name || !form.email || !form.serviceType) return;
    handleFinalSubmit();
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_REFERENCE_IMAGES - referenceImages.length;
    if (remaining <= 0) {
      toast({ title: "Limit reached", description: `You can upload up to ${MAX_REFERENCE_IMAGES} reference images.`, variant: "destructive" });
      return;
    }
    const toAdd = files.slice(0, remaining);
    const invalid = toAdd.filter(f => !f.type.startsWith("image/"));
    const tooBig = toAdd.filter(f => f.type.startsWith("image/") && f.size > 5 * 1024 * 1024);
    if (invalid.length) {
      toast({ title: "Invalid file", description: "Only image files (JPEG, PNG, WebP, etc.) are accepted.", variant: "destructive" });
    }
    if (tooBig.length) {
      toast({ title: "File too large", description: "Each reference image must be under 5 MB.", variant: "destructive" });
    }
    const valid = toAdd.filter(f => f.type.startsWith("image/") && f.size <= 5 * 1024 * 1024);
    if (!valid.length) return;
    const newEntries = valid.map(file => ({ file, preview: URL.createObjectURL(file) }));
    setReferenceImages(prev => [...prev, ...newEntries]);
    setReferenceImageUrls([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (index: number) => {
    setReferenceImages(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
    setReferenceImageUrls([]);
  };

  const handleFinalSubmit = async () => {
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }
    let uploadedUrls = referenceImageUrls;

    // ── Fetch inspiration-link images server-side and merge with file uploads ──
    const inspirationUrls = referenceLinks.split("\n").map((l) => l.trim()).filter(Boolean);
    if (inspirationUrls.length > 0) {
      setImageUploading(true);
      try {
        const res = await fetch("/api/booking-reference-image", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ urls: inspirationUrls }),
        });
        if (res.ok) {
          const data = await res.json();
          const fetched: string[] = data.urls ?? (data.url ? [data.url] : []);
          uploadedUrls = [...uploadedUrls, ...fetched];
        }
      } catch (linkErr) {
        captureException(linkErr);
      } finally {
        setImageUploading(false);
      }
    }

    if (referenceImages.length > 0 && referenceImageUrls.length === 0) {
      setImageUploading(true);
      try {
        const formData = new FormData();
        referenceImages.forEach(({ file }) => formData.append("images", file));
        const res = await fetch("/api/booking-reference-image", { method: "POST", body: formData });
        if (!res.ok) {
          const _fetchErr = new Error("Upload failed");
          captureException(_fetchErr);
          throw _fetchErr;
        }
        const data = await res.json();
        const fileUploaded: string[] = data.urls ?? (data.url ? [data.url] : []);
        uploadedUrls = [...uploadedUrls, ...fileUploaded];
      } catch (_swallowedErr) {
        captureException(_swallowedErr);
        toast({ title: "Image upload failed", description: "Your booking will be submitted without the reference images.", variant: "destructive" });
      } finally {
        setImageUploading(false);
      }
    }
    if (uploadedUrls.length > 0) {
      setReferenceImageUrls(uploadedUrls);
    }
    const fullDescription = [form.description, form.notes].filter(Boolean).join("\n\n--- Additional Notes ---\n");
    const isPiercing = serviceCategory === "piercing";
    const submitData = {
      ...form,
      artistSlug: selectedArtist,
      date: isSoftBooking ? null : selectedDate,
      time: isSoftBooking ? null : selectedTime,
      availabilityNotes: isSoftBooking
        ? [
            availabilityDays.length > 0 ? `Days: ${availabilityDays.join(", ")}` : "Days: Any",
            availabilityTimes.length > 0 ? `Times: ${availabilityTimes.join(", ")}` : "Times: Any",
          ].join(" | ")
        : undefined,
      description: fullDescription || "",
      tattooSize: !isPiercing ? (estSize || null) : null,
      complexity: !isPiercing ? (estComplexity || null) : null,
      placement: !isPiercing ? (estPlacement || null) : null,
      color: !isPiercing ? (estColor || null) : null,
      estimatedCostMin: isPiercing
        ? (piercingEstimate?.low ?? null)
        : (estimateVariant === "C" ? (variantCEstimate?.min ?? null) : (estimate?.low || null)),
      estimatedCostMax: isPiercing
        ? (piercingEstimate?.high ?? null)
        : (estimateVariant === "C" ? (variantCEstimate?.max ?? null) : (estimate?.high || null)),
      estimatedHours: !isPiercing ? (estimateVariant === "C" ? null : (estimate?.hours ?? null)) : null,
      estimateStatus: estimateStatus,
      piercingType: isPiercing ? (piercingType || null) : null,
      jewelryMaterial: isPiercing ? (piercingMaterial || null) : null,
      jewelryGauge: isPiercing ? (piercingGauge || null) : null,
      jewelryStyle: isPiercing ? (piercingStyle || null) : null,
      piercingCount: isPiercing ? (piercingCount || null) : null,
      bodyMapX: bodyMap?.x ?? null,
      bodyMapY: bodyMap?.y ?? null,
      bodyMapView: bodyMap?.view ?? null,
      bodyMapRadius: bodyMap?.radius ?? null,
      bodyMapGender: bodyMap?.gender ?? null,
      tattooSizeInches: bodyMap?.sizeInches ?? null,
      placementNotes: bodyMap?.placementNotes ?? null,
      referenceImageUrl: uploadedUrls[0] || undefined,
      referenceImageUrls: uploadedUrls.length > 0 ? JSON.stringify(uploadedUrls) : undefined,
      ...((isAdmin || isWalkin) ? { sendClientEmail } : {}),
      ...(isWalkin && walkinDepositAmount && parseInt(walkinDepositAmount) > 0 ? { depositAmount: parseInt(walkinDepositAmount) } : {}),
      ...(isArtistPortal ? { confirmMode: artistPortalConfirmMode } : {}),
      ...(isPublic ? getAttribution() : {}),
      ...(isPublic ? { "cf-turnstile-response": turnstileToken } : {}),
      ...(recoveredFromDraftTokenRef.current ? { recoveredFromDraftToken: recoveredFromDraftTokenRef.current } : {}),
      // Task #2505 — Store the occasion/label as a structured tag so admins can
      // filter and identify Flash Day bookings in the admin booking list.
      ...(isFlashDay ? { occasionTag: urlLabel ? `flash:${urlLabel}` : "flash" } : {}),
      // Task #2858 — Referral attribution: if a ?ref=<token> param is present, store it
      // so the studio can attribute this booking back to the referring client.
      ...(isPublic && urlRef ? { referralSource: urlRef } : {}),
      ...(urlDropId && urlClaimToken ? { discountDropId: parseInt(urlDropId, 10), discountClaimToken: urlClaimToken } : {}),
    };
    pendingSubmitRef.current = submitData;
    bookingMutation.mutate(submitData as any);
  };

  const reset = () => {
    setStep(isArtistPortal ? "service-type" : "artist");
    setReturnToStep(null);
    if (!isArtistPortal) setSelectedArtist("");
    setSelectedDate("");
    setSelectedTime("");
    setAvailabilityDays([]);
    setAvailabilityTimes([]);
    setForm({ name: "", email: "", phone: "", serviceType: "", description: "", notes: "", subscribeToNewsletter: false });
    setEstSize(null);
    setEstComplexity(null);
    setEstPlacement(null);
    setEstColor(null);
    setEstSubStep(0);
    setEstAnimating(null);
    setEstShowResults(false);
    setEstResetKey(k => k + 1);
    setEstimateStatus(null);
    setPiercingType("");
    setPiercingMaterial("implant_titanium");
    setPiercingGauge("ask");
    setPiercingStyle("ask");
    setPiercingCount(1);
    setBodyMap(null);
    setArtistAnimating(null);
    setDateAnimating(null);
    setTimeAnimating(null);
    setSendClientEmail(true);
    setWalkinWarnings(null);
    referenceImages.forEach(img => URL.revokeObjectURL(img.preview));
    setReferenceImages([]);
    setReferenceImageUrls([]);
    setSubmittedReferenceImageSignedUrls([]);
    setReferenceLinks("");
    setLinkPreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    idempotencyKeyRef.current = null;
  };

  const artistMinWeeks = selectedArtistAvailability?.minWeeksOut ?? 1;
  const minDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + artistMinWeeks * 7);

  const isPastDate = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return date < todayStart;
  };

  const isTooSoon = (day: number) => {
    if (isAdmin || isWalkin || isArtistPortal) return false;
    const date = new Date(currentYear, currentMonth, day);
    return date < minDate;
  };

  const isBeforeAvailable = (day: number) => {
    if (!selectedArtistAvailability?.nextAvailableDate) return false;
    if (selectedArtistAvailability.status === "available") return false;
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return dateStr < selectedArtistAvailability.nextAvailableDate;
  };


  const blockedDateSet = useMemo(() => {
    return new Set(selectedArtistAvailability?.blockedDates || []);
  }, [selectedArtistAvailability?.blockedDates]);

  const recurringBlockedSet = useMemo(() => {
    return new Set(selectedArtistAvailability?.recurringBlockedDays || []);
  }, [selectedArtistAvailability?.recurringBlockedDays]);

  const isBlockedDate = (day: number) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    if (blockedDateSet.has(dateStr)) return true;
    const weekday = new Date(currentYear, currentMonth, day).getDay();
    return recurringBlockedSet.has(weekday);
  };

  const getFirstAvailableDate = (): Date | null => {
    let candidate = new Date(minDate);
    if (selectedArtistAvailability?.status !== "available" && selectedArtistAvailability?.nextAvailableDate) {
      const avail = new Date(selectedArtistAvailability.nextAvailableDate + "T00:00:00");
      if (avail > candidate) candidate = new Date(avail);
    }
    for (let i = 0; i < 365; i++) {
      if (candidate.getDay() !== 0) {
        const candidateStr = `${candidate.getFullYear()}-${String(candidate.getMonth() + 1).padStart(2, "0")}-${String(candidate.getDate()).padStart(2, "0")}`;
        if (!blockedDateSet.has(candidateStr)) return candidate;
      }
      candidate.setDate(candidate.getDate() + 1);
    }
    return null;
  };

  const firstAvailableDate = getFirstAvailableDate();

  useEffect(() => {
    if (step !== "calendar") return;
    const target = firstAvailableDate;
    if (!target) return;
    const syncKey = `${selectedArtist}|${target.toDateString()}`;
    if (calendarSyncedRef.current === syncKey) return;
    calendarSyncedRef.current = syncKey;
    setCurrentYear(target.getFullYear());
    setCurrentMonth(target.getMonth());
  }, [step, selectedArtist, selectedArtistAvailability]);

  const hasNoAvailableDaysThisMonth = useMemo(() => {
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      if (date < todayStart) continue;
      if (date < minDate) continue;
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      if (blockedDateSet.has(dateStr)) continue;
      if (selectedArtistAvailability?.status !== "available" && selectedArtistAvailability?.nextAvailableDate) {
        if (dateStr < selectedArtistAvailability.nextAvailableDate) continue;
      }
      return false;
    }
    return true;
  }, [currentYear, currentMonth, daysInMonth, today, minDate, selectedArtistAvailability, blockedDateSet]);

  const isToday = (day: number) => {
    return day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();
  };

  const isPastMonth = !isArtistPortal && (currentYear < today.getFullYear() || (currentYear === today.getFullYear() && currentMonth < today.getMonth()));

  const selectedArtistData = artists.find((a) => a.slug === selectedArtist);
  const selectedDateFormatted = selectedDate
    ? new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : "";

  const withServiceTypeStep = !isFlashBooking && !isFlashDay && (!flagsLoaded || availableServiceTypes.length > 1);
  // Service types available in the form step — filtered by the category chosen in the service-type step
  const _formServiceTypes = serviceCategory
    ? getServiceTypeConfig(serviceCategory).formServiceTypes
    : BASE_SERVICE_TYPES;
  const skipPreFormEstimate = hasUrlEstimate || isFlashBooking || isFlashDay || estimateVariant === "D" || !isStepVisible(serviceCategory, "estimate");
  const mainSteps: readonly string[] = getMainSteps(serviceCategory, withServiceTypeStep, skipPreFormEstimate);
  const mainStepLabels: Record<string, string> = { "service-type": "Service", artist: "Artist", calendar: "Date", time: "Preference", estimate: "Estimate", "body-map": "Placement", images: "References", notes: "Design", form: "Details" };
  const currentMainStepIndex = mainSteps.indexOf(step);

  const getEstSubStepOptions = (subStep: EstimateSubStep) => getEstimateOptions(subStep);
  const getEstSubStepTitle = (subStep: EstimateSubStep) => ESTIMATE_STEP_INFO[subStep];

  const getEstValue = (subStep: EstimateSubStep) => {
    switch (subStep) {
      case "size": return estSize;
      case "complexity": return estComplexity;
      case "placement": return estPlacement;
      case "color": return estColor;
    }
  };

  const setEstValue = (subStep: EstimateSubStep, value: string) => {
    switch (subStep) {
      case "size": setEstSize(value); break;
      case "complexity": setEstComplexity(value); break;
      case "placement": setEstPlacement(value); break;
      case "color": setEstColor(value); break;
    }
  };

  const trackBookingEstimate = (s: string, comp: string, pl: string, col: string) => {
    const est = calculateEstimate(s, comp, pl, col, selectedArtist, 0.5);
    fetch("/api/estimates/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source: "booking", artistSlug: selectedArtist, size: s, complexity: comp, placement: pl, color: col, estimateLow: est?.low, estimateHigh: est?.high }),
    }).catch(() => { /* intentionally silent: best-effort side-effect */ });
  };

  const handleEstSelect = (optionId: string) => {
    setEstAnimating(optionId);
    setEstValue(ESTIMATE_SUB_STEPS[estSubStep], optionId);
    setTimeout(() => {
      setEstAnimating(null);
      if (estSubStep < ESTIMATE_SUB_STEPS.length - 1) {
        setEstSubStep(estSubStep + 1);
      } else {
        setEstShowResults(true);
        const vals: Record<string, string | null> = { size: estSize, complexity: estComplexity, placement: estPlacement, color: estColor };
        vals[ESTIMATE_SUB_STEPS[estSubStep]] = optionId;
        if (vals.size && vals.complexity && vals.placement && vals.color) {
          trackBookingEstimate(vals.size, vals.complexity, vals.placement, vals.color);
        }
      }
    }, 400);
  };

  const estGoBack = () => {
    if (estShowResults) {
      setEstShowResults(false);
      return;
    }
    if (estSubStep > 0) {
      setEstSubStep(estSubStep - 1);
    } else {
      setStep(isSoftBooking ? "availability" : "time");
    }
  };

  // Variant B: skip estimate prompt
  const handleEstimateSkip = () => {
    trackSubFunnelEvent("estimate-prompt:skipped");
    setEstimateStatus("completed");
    if (returnToStep) {
      setStep(returnToStep);
      setReturnToStep(null);
    } else {
      setStep("body-map");
    }
  };

  // Variant C: single price-range pick handler
  const handleVariantCSelect = (bucketId: string) => {
    const bucket = PRICE_RANGE_BUCKETS.find((b) => b.id === bucketId);
    if (!bucket) return;
    setVariantCAnimating(bucketId);
    setVariantCBucket(bucketId);
    setTimeout(() => {
      setVariantCAnimating(null);
      setEstimateStatus("completed");
      if (returnToStep) {
        setStep(returnToStep);
        setReturnToStep(null);
      } else {
        setStep("body-map");
      }
    }, 400);
  };

  // Derive cost min/max for variant C from the selected bucket (used in handleFinalSubmit)
  const variantCEstimate = variantCBucket
    ? PRICE_RANGE_BUCKETS.find((b) => b.id === variantCBucket) ?? null
    : null;

  const estRestart = () => {
    setEstSize(null);
    setEstComplexity(null);
    setEstPlacement(null);
    setEstColor(null);
    setEstSubStep(0);
    setEstAnimating(null);
    setEstShowResults(false);
    setEstResetKey(k => k + 1);
  };

  const estProgress = estShowResults ? 100 : (estSubStep / ESTIMATE_SUB_STEPS.length) * 100;

  const bookingBaseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const bookingJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Tattoo Booking — ${studio?.name ?? "Studio"}`,
    serviceType: "Tattoo appointment booking",
    url: `${bookingBaseUrl}/booking`,
    description: `Book a tattoo, piercing, or consultation appointment with ${studio?.name ?? "the studio"}. Choose your artist, date, and time online.`,
    provider: {
      "@type": "TattooParlor",
      name: studio?.name,
      url: bookingBaseUrl,
      ...(studio?.city && studio?.state
        ? { address: { "@type": "PostalAddress", addressLocality: studio.city, addressRegion: studio.state } }
        : {}),
    },
    potentialAction: {
      "@type": "ReserveAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${bookingBaseUrl}/booking`,
        actionPlatform: [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/MobileWebPlatform",
        ],
      },
      result: { "@type": "Reservation", name: "Tattoo Appointment" },
    },
  };

  return (
    <div className={`min-h-screen pt-12 ${isPublic && step !== "done" ? "pb-20" : "pb-12"}`}>
      {isPublic && <JsonLd id="booking-jsonld" data={bookingJsonLd} />}
      <div className="container mx-auto px-6 max-w-5xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {isPublic && (
            <Link
              href="/"
              className="inline-flex items-center text-white bg-black/80 border border-white/10 hover:bg-accent hover:border-accent hover:text-white active:scale-[0.97] active:brightness-90 transition-all uppercase tracking-widest text-xs gap-2 group px-6 py-3 mb-8 rounded-full"
              data-testid="link-back-home"
            >
              <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Back to Studio
            </Link>
          )}

          {isAdmin && (
            <div className="flex items-center gap-3 mb-6 rounded-xl px-5 py-3 border border-blue-500/50 bg-blue-950/90" data-testid="admin-booking-mode-banner">
              <span className="text-blue-400 text-lg">📋</span>
              <div>
                <p className="text-xs font-bold text-blue-300 uppercase tracking-wider">Admin Entry Mode</p>
                <p className="text-xs text-blue-400">Bypasses daily limits and advance notice requirements. All entries are logged in the audit trail.</p>
              </div>
            </div>
          )}

          {isWalkin && (
            <div className="flex items-center gap-3 mb-6 rounded-xl px-5 py-3 border border-purple-500/30 bg-purple-500/10" data-testid="walkin-booking-mode-banner">
              <span className="text-purple-400 text-lg">🚶</span>
              <div>
                <p className="text-xs font-bold text-purple-300 uppercase tracking-wider">Walk-In Booking</p>
                <p className="text-xs text-purple-400/70">Creates a confirmed booking immediately. Release form can be sent after.</p>
              </div>
            </div>
          )}

          {isArtistPortal && (
            <div className="flex items-center gap-3 mb-6 rounded-xl px-5 py-3 border border-violet-500/30 bg-violet-500/10" data-testid="artist-portal-booking-mode-banner">
              <span className="text-violet-400 text-lg">✏️</span>
              <div>
                <p className="text-xs font-bold text-violet-300 uppercase tracking-wider">Artist Portal — Add Appointment</p>
                <p className="text-xs text-violet-400/70">Use for brand-new clients or to enter an appointment you've already agreed to — DMs, referrals, previous system, etc.</p>
              </div>
            </div>
          )}

          <FeatureFlagZone flagKey="deposits_required_for_booking">
          <AnimatePresence mode="wait">
            {showDepositGate ? (
              <motion.div
                key="deposit-gate"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.3 }}
                className="rounded-2xl p-8 md:p-10 mb-6 bg-black/80 backdrop-blur-sm border border-amber-500/30"
                data-testid="deposit-gate-notice"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-amber-400 text-2xl">🔒</span>
                  <h2 className="text-amber-400 uppercase tracking-[0.25em] text-xs font-bold font-subheading">Online Bookings Temporarily Paused</h2>
                </div>
                <h1 className="text-3xl md:text-4xl font-heading mb-4 text-white tracking-wide">
                  We'll be back online shortly
                </h1>
                <p className="text-white/70 text-lg font-light max-w-2xl leading-relaxed mb-6">
                  The studio is completing its secure payment setup. Online bookings will reopen once that's in place — please reach out directly to reserve your appointment in the meantime.
                </p>
                {studio.profile !== "agency" && (
                  <div className="flex flex-col sm:flex-row gap-3">
                    {config.phone && (
                      <a
                        href={`tel:${config.phone}`}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30 transition-all font-subheading text-sm uppercase tracking-widest"
                        data-testid="link-deposit-gate-phone"
                      >
                        📞 Call Us
                      </a>
                    )}
                    {config.contactEmail && (
                      <a
                        href={`mailto:${config.contactEmail}`}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 transition-all font-subheading text-sm uppercase tracking-widest"
                        data-testid="link-deposit-gate-email"
                      >
                        ✉️ Email Us
                      </a>
                    )}
                  </div>
                )}
              </motion.div>
            ) : isPublic && !bookingStarted ? (
              <motion.div
                key="welcome-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16, scale: 0.97 }}
                transition={{ duration: 0.3 }}
                className="rounded-2xl p-8 md:p-10 mb-6 bg-black/80 backdrop-blur-sm border border-white/10"
                data-testid="booking-welcome-card"
              >
                <h2 className="text-violet-300 uppercase tracking-[0.3em] text-xs font-bold mb-4 font-subheading">
                  {t("welcome.eyebrow")}
                </h2>
                <h1 className="text-4xl md:text-6xl font-heading mb-5 text-white tracking-wide" data-testid="text-booking-title">
                  {t("welcome.heading")} <span className="text-accent italic">{t("welcome.heading_accent")}</span>
                </h1>
                <p className="text-muted-foreground text-lg font-light max-w-2xl leading-relaxed mb-6">
                  {t("welcome.subheading")}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8 max-w-2xl">
                  {[
                    { icon: <User size={13} />, label: t("welcome.step_service") },
                    { icon: <Calendar size={13} />, label: t("welcome.step_date") },
                    { icon: <Sparkles size={13} />, label: t("welcome.step_estimate") },
                    { icon: <ClipboardList size={13} />, label: t("welcome.step_placement") },
                    { icon: <CheckCircle size={13} />, label: t("welcome.step_submit") },
                  ].map(({ icon, label }, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm text-white/60">
                      <span className="text-accent/70 shrink-0">{icon}</span>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
                <div className="inline-flex items-center gap-2 text-sm text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-2 mb-8">
                  <Phone size={14} className="shrink-0" />
                  <span>{t("welcome.contact_note")}</span>
                </div>
                <div>
                  <button
                    onClick={() => {
                      setBookingStarted(true);
                      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
                    }}
                    className="btn-cta-glow inline-flex items-center gap-3 bg-accent hover:brightness-110 active:scale-[0.97] transition-all text-white font-heading uppercase tracking-widest text-sm px-8 py-4 rounded-xl"
                    data-testid="button-booking-get-started"
                  >
                    {t("welcome.cta_get_started")} <ArrowRight size={16} />
                  </button>
                </div>
              </motion.div>
            ) : !isPublic ? (
              /* Step header for admin / walk-in modes only */
              <motion.div
                key="step-header"
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="rounded-2xl p-6 md:p-8 mb-6 bg-black/80 backdrop-blur-sm border border-white/10"
              >
                <h2 className="text-violet-300 uppercase tracking-[0.3em] text-xs font-bold mb-2 font-subheading">
                  {isAdmin ? "Admin — Add Appointment" : "Walk-In — Create Booking"}
                </h2>
                <h1 className="text-3xl md:text-5xl font-heading mb-3 text-white tracking-wide" data-testid="text-booking-title">
                  {isAdmin ? <>Add an <span className="text-accent italic">Appointment</span></> : <>Walk-In <span className="text-accent italic">Booking</span></>}
                </h1>
                <p className="text-muted-foreground text-base font-light max-w-2xl leading-relaxed">
                  {step === "service-type" && "Tell us what you're looking to book today."}
                  {step === "artist" && (isFlashBooking ? `Booking flash design: "${urlFlash}" — choose your artist.` : "Choose which artist you'd like to work with.")}
                  {step === "flash-info" && "What to expect when booking a flash design."}
                  {step === "calendar" && "Pick the appointment date."}
                  {step === "time" && "Select the appointment time."}
                  {step === "availability" && "Let us know when you're generally free and we'll find a time that works."}
                  {step === "estimate" && "Get a price estimate based on size, complexity, placement, and color."}
                  {step === "body-map" && "Show the client's tattoo placement on the body map."}
                  {step === "images" && "Upload any reference images for the design or placement."}
                  {step === "notes" && "Add any design description or notes for this booking."}
                  {step === "form" && "Enter the client's contact details to submit."}
                  {step === "duplicate-check" && "We found an existing request that might be the same booking."}
                  {step === "walkin-warnings" && "Review these flags before confirming the walk-in."}
                  {step === "done" && (isAdmin ? "Appointment added successfully!" : "Walk-in booked and confirmed!")}
                </p>
              </motion.div>
            ) : null /* public + bookingStarted: no persistent header, steps flow directly */}
          </AnimatePresence>
          </FeatureFlagZone>

        </motion.div>

        <AnimatePresence>
          {resumeCandidate && (
            <motion.div
              key="resume-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 flex items-center justify-center px-6" // @modal-overflow-ok draft resume prompt: max-w-md, 1 heading + 1 paragraph + 2 buttons — bounded content
              data-testid="draft-resume-prompt"
              style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(10px)" }}
              onClick={handleResumeBackdropClick}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.93, y: 12 }}
                animate={resumeCardControls}
                exit={{ opacity: 0, scale: 0.93, y: 12 }}
                transition={{ duration: 0.25, delay: 0.05 }}
                className="w-full max-w-md rounded-3xl p-8 bg-gray-950 border border-accent/40 shadow-2xl"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                    <Clock size={22} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-violet-300 text-xs font-subheading uppercase tracking-widest mb-0.5">Saved Progress</p>
                    <h3 className="text-white text-xl font-heading tracking-wide leading-tight">Resume your booking?</h3>
                  </div>
                </div>

                <p className="text-white/60 text-sm leading-relaxed">
                  You left off <span className="text-white/90 font-medium">{timeSince(resumeCandidate.savedAt)}</span> — your progress is saved and ready.
                </p>

                <div className="mt-3 h-5">
                  <AnimatePresence>
                    {resumeOutsideHint && (
                      <motion.p
                        key="outside-hint"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="text-xs text-amber-400/80 font-subheading tracking-wide"
                      >
                        Please make a choice to continue ↓
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                <div className="flex flex-col gap-3 mt-4">
                  <button
                    onClick={handleResumeDraft}
                    data-testid="button-resume-draft"
                    className="flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-full bg-accent text-white font-subheading text-xs uppercase tracking-widest hover:brightness-110 active:scale-[0.97] transition-all"
                  >
                    <RotateCcw size={14} /> Resume where I left off
                  </button>
                  <button
                    onClick={handleStartFresh}
                    data-testid="button-start-fresh"
                    className="flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-full bg-white/5 border border-white/15 text-white/70 font-subheading text-xs uppercase tracking-widest hover:bg-white/10 hover:text-white active:scale-[0.97] transition-all"
                  >
                    Start fresh
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div ref={stepContentRef} style={{ scrollMarginTop: "132px" }}>
        {(!isPublic || bookingStarted) && <AnimatePresence mode="wait">
          {step === "service-type" && (
            <motion.div key="service-type" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="rounded-2xl p-8 bg-black/80 backdrop-blur-sm border border-white/10">
                <h2 className="text-xl font-heading tracking-wide mb-2 text-white">What are you booking?</h2>
                <p className="text-sm text-white/60 mb-8">Choose your service type to get started</p>
                <div className={`grid gap-4 ${availableServiceTypes.length > 1 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 max-w-xs mx-auto"}`} data-testid="booking-category-selector">
                  {availableServiceTypes.map(({ slug, displayName, icon, description: desc }) => {
                    const isCatAnimating = serviceCategoryAnimating === slug;
                    return (
                    <button
                      key={slug}
                      data-testid={`button-category-${slug}`}
                      onClick={() => {
                        if (serviceCategoryAnimating !== null) return;
                        setServiceCategoryAnimating(slug);
                        setTimeout(() => {
                          setServiceCategoryAnimating(null);
                          const id = slug;
                          setServiceCategory(id);
                          const defaultSvc = SERVICE_TYPE_CONFIG[id].formServiceTypes[0] ?? "";
                          setForm(f => ({ ...f, serviceType: defaultSvc }));
                          if (isArtistPortal) {
                            setStep("calendar");
                          } else {
                            let artistStillValid = false;
                            if (selectedArtist) {
                              const preselected = artists.find(a => a.slug === selectedArtist);
                              const svcTypes = preselected?.serviceTypes ?? ["Tattoo"];
                              const incompatible = !svcTypes.includes(SERVICE_TYPE_CONFIG[id].displayName);
                              if (incompatible) { setSelectedArtist(""); }
                              else { artistStillValid = true; }
                            }
                            setStep(artistStillValid ? (isSoftBooking ? "availability" : "calendar") : "artist");
                          }
                        }, 180);
                      }}
                      className={`group flex flex-col items-center justify-center gap-3 p-8 rounded-2xl border transition-all text-center min-h-[44px] ${
                        isCatAnimating
                          ? "border-accent bg-accent/15 scale-[0.97]"
                          : serviceCategoryAnimating !== null
                          ? "border-white/5 opacity-50"
                          : "border-white/10 bg-white/4 hover:border-accent/60 hover:bg-accent/5 active:scale-[0.97] active:border-accent active:bg-accent/10 cursor-pointer"
                      }`}
                    >
                      <span className="text-4xl">{icon}</span>
                      <span className="text-lg font-heading tracking-wide text-white group-hover:text-accent transition-colors">{displayName}</span>
                      <span className="text-xs text-white/60 font-light">{desc}</span>
                    </button>
                  ); })}
                </div>
              </div>
            </motion.div>
          )}

          {step === "artist" && (
            <motion.div key="artist" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="rounded-2xl p-8 bg-black/80 backdrop-blur-sm border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-heading tracking-wide text-white">Choose Your Artist</h2>
                    <p className="text-sm text-white/60 mt-1">Select the artist you'd like to work with</p>
                  </div>
                  {withServiceTypeStep && serviceCategory && (
                    <button
                      onClick={() => setStep("service-type")}
                      className="inline-flex items-center uppercase tracking-widest text-xs gap-2 group px-6 py-3 rounded-full transition-all text-white bg-accent border border-accent hover:brightness-110 active:scale-[0.97] active:brightness-90 cursor-pointer font-bold font-subheading min-h-[44px]"
                      data-testid="button-back-to-service-type"
                    >
                      <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                      {serviceCategory ? getServiceTypeConfig(serviceCategory).displayName : ""}
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  {artists.map((artist) => {
                    const isAnimating = artistAnimating === artist.slug;
                    const avail = availabilityMap.get(artist.slug);
                    const availStatus = avail?.status || "available";
                    const availDate = avail?.nextAvailableDate;
                    const availDateLabel = availDate
                      ? new Date(availDate + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                      : null;
                    const isBookedOut = availStatus === "booked";
                    const artistMinDate = (() => {
                      const weeksOut = avail?.minWeeksOut ?? 1;
                      const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + weeksOut * 7);
                      return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
                    })();
                    const artistServiceTypes = artist.serviceTypes ?? ["Tattoo"];
                    const isIncompatible = serviceCategory !== "" && (
                      !artistServiceTypes.includes(getServiceTypeConfig(serviceCategory).displayName)
                    );
                    const isDisabled = isBookedOut || isIncompatible;
                    return (
                      <div
                        key={artist.slug}
                        className={`w-full text-left border rounded-lg p-5 transition-all bg-black/80 backdrop-blur-sm ${
                          isIncompatible
                            ? "border-white/5 opacity-70 cursor-not-allowed"
                            : isBookedOut
                            ? `border-white/5 opacity-70 cursor-pointer ${tappedBookedOutSlug === artist.slug ? "border-red-500/20 bg-red-500/5" : "hover:border-red-500/10"}`
                            : isAnimating
                            ? "border-accent bg-accent/20 text-white scale-[0.98]"
                            : artistAnimating !== null
                            ? "border-white/5 text-white/40 opacity-60"
                            : "border-white/10 text-white/80 group cursor-pointer hover:text-white hover:border-accent/50 hover:bg-accent/5 active:bg-accent/20 active:border-accent active:text-white active:scale-[0.98]"
                        }`}
                        onClick={() => {
                          if (isBookedOut && !isIncompatible) {
                            setTappedBookedOutSlug(prev => prev === artist.slug ? null : artist.slug);
                          } else if (!isDisabled && artistAnimating === null) {
                            setTappedBookedOutSlug(null);
                            selectArtist(artist.slug);
                          }
                        }}
                        role="button"
                        data-testid={`button-select-artist-${artist.slug}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <img
                              src={artist.image}
                              alt={artist.name}
                              className={`w-12 h-12 object-cover shrink-0 ${isDisabled ? "grayscale" : ""}`}
                              loading="lazy"
                              srcSet={buildImageSrcSet(artist.image)}
                              sizes="48px"
                              style={{
                                borderRadius: `${config.artistCardRadius}%`,
                                border: config.artistCardBorderWidth > 0
                                  ? `${Math.max(1, config.artistCardBorderWidth * 0.5)}px solid ${config.magikGlowColor1}`
                                  : "1px solid rgba(255,255,255,0.1)",
                                objectPosition: `${(config.artistCardPositions?.[artist.slug]?.x ?? 50)}% ${(config.artistCardPositions?.[artist.slug]?.y ?? 50)}%`,
                              }}
                            />
                            <div>
                              <span className="font-subheading font-semibold text-sm tracking-wide block">{artist.name}</span>
                              <span className="text-xs text-white/60 mt-1 block">{artist.role}</span>
                              {isIncompatible && serviceCategory && (
                                <span className="inline-flex items-center gap-1 text-[10px] mt-1.5 px-2 py-0.5 rounded-full bg-white/10 text-white/80 border border-white/20" data-testid={`badge-incompatible-${artist.slug}`}>
                                  {`Doesn't offer ${getServiceTypeConfig(serviceCategory).displayName.toLowerCase()} services`}
                                </span>
                              )}
                              {!isIncompatible && isBookedOut && (
                                <span className="inline-flex items-center gap-1 text-[10px] mt-1.5 px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20" data-testid={`badge-availability-${artist.slug}`}>
                                  <AlertCircle size={10} />
                                  Not accepting new bookings
                                  {availDateLabel && <> · Opens {availDateLabel}</>}
                                </span>
                              )}
                              {!isIncompatible && availStatus === "limited" && (
                                <span className="inline-flex items-center gap-1 text-[10px] mt-1.5 px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400 border border-yellow-500/20" data-testid={`badge-availability-${artist.slug}`}>
                                  <Clock size={10} /> Limited availability
                                  {availDateLabel && <> · Until {availDateLabel}</>}
                                </span>
                              )}
                              {!isIncompatible && availStatus === "available" && (
                                <span className="inline-flex items-center gap-1 text-[10px] mt-1.5 px-2 py-0.5 rounded-full bg-accent/10 text-violet-300 border border-accent/20" data-testid={`badge-first-available-${artist.slug}`}>
                                  <Clock size={10} /> First available {artistMinDate}
                                </span>
                              )}
                            </div>
                          </div>
                          {isAnimating ? (
                            <span className="w-5 h-5 rounded-full bg-accent flex items-center justify-center shrink-0">
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </span>
                          ) : isBookedOut && !isIncompatible ? (
                            tappedBookedOutSlug === artist.slug ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setWaitlistTargetArtistSlug(artist.slug);
                                  setShowWaitlistForm(true);
                                  setTappedBookedOutSlug(null);
                                }}
                                className="flex items-center gap-1.5 text-[11px] font-semibold text-red-400 border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 px-3 py-1.5 rounded-full transition-colors shrink-0 min-h-[44px]"
                                data-testid={`button-waitlist-${artist.slug}`}
                              >
                                <Bell size={11} /> Join Waitlist
                              </button>
                            ) : (
                              <ChevronRight size={14} className="text-white/20 shrink-0" />
                            )
                          ) : !isDisabled ? (
                            <ArrowRight size={16} className="text-muted-foreground group-hover:text-accent transition-colors opacity-0 group-hover:opacity-100 shrink-0" />
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {step === "flash-info" && (
            <motion.div key="flash-info" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="max-w-2xl mx-auto rounded-2xl p-8 md:p-10 bg-black/80 backdrop-blur-sm border border-white/10" data-testid="flash-info-card">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles size={20} className="text-accent shrink-0" />
                  <h2 className="text-xl font-heading tracking-wide text-white">Flash Design Booking</h2>
                </div>

                {urlFlash && (
                  <div className="flex items-center gap-3 mb-6 px-4 py-3 rounded-xl bg-accent/10 border border-accent/25" data-testid="flash-info-design-name">
                    <Sparkles size={14} className="text-accent shrink-0" />
                    <p className="text-sm text-white/90">
                      You're booking: <span className="font-semibold text-accent">{urlFlash}</span>
                    </p>
                  </div>
                )}

                {/* What is a Flash Tattoo — collapsible */}
                <div className="mb-6 rounded-xl border border-white/10 overflow-hidden" data-testid="flash-what-is">
                  <button
                    type="button"
                    onClick={() => setFlashWhatIsOpen(o => !o)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors"
                    data-testid="button-flash-what-is-toggle"
                    aria-expanded={flashWhatIsOpen}
                  >
                    <span className="text-sm font-semibold text-white/80 tracking-wide">What is a Flash Tattoo?</span>
                    <ChevronDown size={15} className={`text-white/40 shrink-0 transition-transform duration-200 ${flashWhatIsOpen ? "rotate-180" : ""}`} />
                  </button>

                  {flashWhatIsOpen && (
                    <div className="px-4 pb-5 pt-1 border-t border-white/10" data-testid="flash-what-is-body">
                      {/* Flash sheet illustration */}
                      <div className="mb-4 rounded-lg overflow-hidden">
                        <img
                          src="/storage/images/img_4623.webp"
                          alt="Tattoo artwork — flash designs are ready-to-ink originals from the artist"
                          className="w-full h-40 object-cover object-center"
                          loading="lazy"
                          decoding="async"
                          width={1200}
                          height={800}
                        />
                      </div>

                      <p className="text-sm text-white/60 leading-relaxed">
                        Flash tattoos are pre-drawn, one-of-a-kind designs — typically displayed on a sheet for clients to choose from. Each piece is finished artwork, ready to tattoo as-is. Unlike custom work, there's no back-and-forth design process: what you see is exactly what you get, inked straight from the artist's original drawing.
                      </p>
                    </div>
                  )}
                </div>

                <p className="text-sm text-white/60 mb-6 leading-relaxed">
                  Here's what that means for your booking:
                </p>

                <ul className="space-y-3 mb-8" data-testid="flash-info-list">
                  {[
                    { text: "Tattooed exactly as shown — no modifications." },
                    { text: "No reference images or design brief needed." },
                    { text: "Size is fixed. Placement is discussed with your artist on the day." },
                  ].map(({ text }, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle size={15} className="text-accent shrink-0 mt-0.5" />
                      <span className="text-sm text-white/80 leading-snug">{text}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex items-center justify-center">
                  <button
                    onClick={() => setStep("calendar")}
                    className="btn-cta-glow bg-accent hover:bg-accent/80 active:scale-[0.97] active:bg-accent/70 inline-flex items-center justify-center gap-2 w-full px-8 py-4 rounded-full text-white uppercase tracking-widest text-xs font-bold font-subheading transition-all duration-300"
                    data-testid="button-flash-info-continue"
                    type="button"
                  >
                    Pick a Date <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {step === "calendar" && (
            <motion.div key="calendar" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              {isFlashDay && (
                <div className="mb-4 flex items-start gap-3 px-5 py-4 rounded-2xl bg-amber-500/10 border border-amber-500/25" data-testid="flash-day-banner">
                  <Sparkles size={16} className="text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-300">
                      ⚡ Flash Day{urlLabel ? ` — ${urlLabel}` : ""}
                    </p>
                    <p className="text-xs text-amber-200/70 mt-0.5 leading-relaxed">
                      Same-day flash designs — limited slots, first-come first-served. A deposit is required to secure your spot.
                    </p>
                  </div>
                </div>
              )}
              <div className="rounded-2xl p-8 bg-black/80 backdrop-blur-sm border border-white/10">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-heading tracking-wide text-white">Pick Your Preferred Date</h2>
                    {selectedArtistData && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Requesting time with <span className="text-accent">{selectedArtistData.name}</span>
                      </p>
                    )}
                  </div>
                  {!isArtistPortal && !isFlashBooking && (
                    <button onClick={() => setStep("artist")} className="inline-flex items-center justify-center bg-accent text-white hover:bg-accent/80 transition-colors uppercase tracking-widest text-xs font-bold font-subheading px-5 py-2.5 rounded-full" data-testid="button-change-artist">
                      Change Artist
                    </button>
                  )}
                </div>

                {(() => {
                  const hasAvailIssue = !!(selectedArtistAvailability && selectedArtistAvailability.status !== "available" && selectedArtistAvailability.nextAvailableDate);
                  const relWeeks = hasAvailIssue ? getRelativeWeeks(selectedArtistAvailability!.nextAvailableDate!) : null;
                  return (
                    <div
                      className={`rounded-xl p-3 mb-4 border ${hasAvailIssue ? "bg-amber-500/10 border-amber-500/20" : "bg-accent/10 border-accent/20"}`}
                      data-testid={hasAvailIssue ? "availability-banner" : "calendar-info-banner"}
                    >
                      {hasAvailIssue && (
                        <div className="flex items-start gap-2 mb-2">
                          <AlertCircle size={14} className="shrink-0 mt-0.5 text-amber-400" />
                          <p className="text-sm text-amber-200 font-medium leading-snug">
                            {selectedArtistData?.name}{" "}{selectedArtistAvailability!.status === "booked" ? "is fully booked until" : "has limited availability until"}{" "}
                            <span className="text-white">{new Date(selectedArtistAvailability!.nextAvailableDate! + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                            {relWeeks && <span className="text-amber-400/70"> · {relWeeks}</span>}
                          </p>
                        </div>
                      )}
                      <p className="text-xs text-white/50 flex items-start gap-1.5">
                        <Info size={11} className="shrink-0 mt-0.5 text-accent" />
                        <span>
                          {!hasAvailIssue && <>Booked at least <span className="text-white/80 font-medium">1 week</span> in advance · </>}Your artist will contact you (via email or phone) to confirm
                          {hasNoAvailableDaysThisMonth && <span className="text-white/60"> · no dates available this month</span>}
                        </span>
                      </p>
                    </div>
                  );
                })()}

                <div className="max-w-md mx-auto">
                  <div className="flex items-center justify-between mb-6">
                    <button
                      onClick={prevMonth}
                      disabled={isPastMonth}
                      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-accent hover:border-accent transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid="button-prev-month"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <h3 className="font-heading text-lg text-white" data-testid="text-current-month">{monthName}</h3>
                    <button
                      onClick={nextMonth}
                      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-accent hover:border-accent transition-colors"
                      data-testid="button-next-month"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>

                  <div className="grid grid-cols-7 sm:grid-cols-7 gap-1 mb-2">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                      <div key={day} className="text-center text-xs text-muted-foreground uppercase tracking-wider font-subheading py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 sm:grid-cols-7 gap-1">
                    {Array.from({ length: firstDay }).map((_, i) => (
                      <div key={`empty-${i}`} />
                    ))}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      const past = !isArtistPortal && isPastDate(day);
                      const tooSoon = isTooSoon(day);
                      const beforeAvail = !isArtistPortal && isBeforeAvailable(day);
                      const blocked = isBlockedDate(day);
                      const disabled = past || tooSoon || beforeAvail || blocked;
                      const isAnimating = dateAnimating === day;
                      const dayStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                      const isHighlighted = highlightDay === dayStr;

                      return (
                        <button
                          key={day}
                          onClick={() => !disabled && dateAnimating === null ? selectDate(day) : undefined}
                          disabled={disabled || dateAnimating !== null}
                          className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm relative transition-all ${
                            isToday(day) && !isAnimating && !isHighlighted ? "ring-2 ring-accent/50" : ""
                          } ${
                            isHighlighted
                              ? "bg-accent text-white ring-2 ring-accent shadow-[0_0_16px_rgba(139,92,246,0.7)] animate-pulse scale-110 z-10"
                              : isAnimating
                                ? "bg-accent text-white scale-[0.92] ring-2 ring-accent"
                                : blocked
                                  ? "text-red-400/30 cursor-not-allowed bg-red-900/10 line-through"
                                  : beforeAvail
                                    ? "text-amber-400/30 cursor-not-allowed bg-amber-900/5"
                                    : disabled
                                      ? "text-white/20 cursor-not-allowed"
                                      : dateAnimating !== null
                                        ? "text-white/20"
                                        : "text-white hover:bg-accent/20 hover:text-accent cursor-pointer"
                          }`}
                          data-testid={`button-day-${day}`}
                        >
                          {isAnimating ? (
                            <svg width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          ) : (
                            <span className="font-medium">{day}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex items-center gap-6 mt-6 text-xs text-muted-foreground justify-center flex-wrap">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded border-2 border-accent/50" />
                      <span>Today</span>
                    </div>
                    {selectedArtistAvailability?.status !== "available" && selectedArtistAvailability?.nextAvailableDate && (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-amber-400/50" />
                        <span>Not yet available</span>
                      </div>
                    )}
                  </div>

                  {selectedArtistAvailability?.status === "booked" && (
                    <div className="rounded-xl mt-6 border border-accent/30 overflow-hidden" data-testid="waitlist-panel">
                      {(waitlistStatus === "done" || waitlistStatus === "alreadyOnList") ? (
                        <div className="p-5 bg-accent/10 flex items-start gap-3" data-testid="waitlist-success">
                          <CheckCircle size={18} className="shrink-0 mt-0.5 text-accent" />
                          <div>
                            <p className="text-sm font-semibold text-white mb-1">
                              {waitlistStatus === "alreadyOnList" ? "You're already on the waitlist!" : "You're on the waitlist!"}
                            </p>
                            <p className="text-xs text-white/60">We'll email you as soon as a slot opens up. You can still proceed to book a date below.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 px-5 py-4 bg-accent/8">
                          <Bell size={16} className="text-accent shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-white">This artist is currently booked</p>
                            <p className="text-xs text-white/50">Join the waitlist to be notified when a slot opens up</p>
                          </div>
                          <button
                            onClick={() => setShowWaitlistForm(true)}
                            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold font-subheading uppercase tracking-widest bg-accent text-white hover:bg-accent/80 active:scale-95 transition-all"
                            data-testid="button-toggle-waitlist"
                          >
                            <Bell size={12} /> Join Waitlist
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {step === "time" && (
            <motion.div key="time" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="rounded-2xl p-8 bg-black/80 backdrop-blur-sm border border-white/10">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-heading tracking-wide text-white">Preferred Time of Day</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      <span className="text-accent">{selectedArtistData?.name}</span> — {selectedDateFormatted}
                    </p>
                  </div>
                  <button onClick={() => setStep("calendar")} className="inline-flex items-center justify-center bg-accent text-white hover:bg-accent/80 transition-colors uppercase tracking-widest text-xs font-bold font-subheading px-5 py-2.5 rounded-full" data-testid="button-change-date">
                    Change Date
                  </button>
                </div>

                <p className="text-sm text-white/50 mb-2 flex items-center gap-2">
                  <Clock size={14} /> We can accommodate your schedule.
                </p>
                <p className="text-xs text-white/55 mb-6">
                  Pick the time that works best for you. If your exact time isn't listed, choose the closest one — we'll discuss and confirm the details when we reach out.
                </p>

                <div className="space-y-5 max-w-lg mx-auto">
                  {bookedTimesFetching ? (
                    <p className="text-sm text-white/60 text-center py-4">Checking availability…</p>
                  ) : availableTimeSlotGroups.length === 0 ? (
                    <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                      <p className="text-white/70 text-sm font-medium mb-1">No open slots on this day</p>
                      <p className="text-white/60 text-xs mb-4">This date is fully booked. Pick a different date or join the waitlist for this specific date.</p>
                      <div className="flex flex-col gap-2 items-center">
                        <button
                          onClick={() => setStep("calendar")}
                          className="inline-flex items-center justify-center bg-accent text-white hover:bg-accent/80 transition-colors uppercase tracking-widest text-xs font-bold px-5 py-2.5 rounded-full"
                          data-testid="button-pick-different-date"
                        >
                          Pick a Different Date
                        </button>
                        {selectedArtistAvailability?.status === "available" && (
                          <button
                            onClick={() => {
                              setWaitlistTargetArtistSlug(selectedArtist);
                              setShowWaitlistForm(true);
                            }}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-300 hover:text-violet-200 underline underline-offset-2 transition-colors"
                            data-testid="button-waitlist-this-date"
                          >
                            <Bell size={11} /> Join waitlist for {selectedDateFormatted}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                  availableTimeSlotGroups.map((group) => (
                    <div key={group.group}>
                      <div className="text-xs text-white/60 uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5">
                        <span>{group.icon}</span> {group.group}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                        {group.times.map((t) => {
                          const isAnimating = timeAnimating === t;
                          return (
                            <button
                              key={t}
                              onClick={() => timeAnimating === null ? selectTime(t) : undefined}
                              disabled={timeAnimating !== null}
                              className={`py-3 px-2 rounded-xl text-center transition-all border-2 focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none text-sm font-medium ${
                                isAnimating
                                  ? "border-accent bg-accent text-white scale-[0.96]"
                                  : timeAnimating !== null
                                    ? "border-white/5 text-white/30 opacity-60"
                                    : "border-white/10 text-white hover:border-accent hover:bg-accent/10"
                              }`}
                              data-testid={`button-time-${t.replace(/\s/g, "-").toLowerCase()}`}
                            >
                              {t}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))
                  )}

                  {!bookedTimesFetching && availableTimeSlotGroups.length > 0 && (
                  <div className="pt-3 border-t border-white/10 max-w-lg mx-auto">
                    <p className="text-xs text-white/50 mb-3 text-center">Not sure what time works? Let your artist decide.</p>
                    <button
                      onClick={() => {
                        if (timeAnimating !== null) return;
                        trackSubFunnelEvent("time:flexible");
                        setSelectedTime("Flexible — my artist can pick");
                        setTimeout(() => {
                          if (returnToStep) {
                            setStep(returnToStep);
                            setReturnToStep(null);
                          } else if (skipPreFormEstimate) {
                            setStep("body-map");
                          } else {
                            setEstSubStep(0);
                            setEstShowResults(false);
                            setEstimateStatus("completed");
                            setStep("estimate");
                          }
                        }, 300);
                      }}
                      disabled={timeAnimating !== null}
                      className={`w-full py-3.5 px-4 rounded-xl text-center border-2 transition-all text-sm font-semibold min-h-[44px] ${
                        selectedTime === "Flexible — my artist can pick"
                          ? "border-accent bg-accent text-white scale-[0.98]"
                          : "border-accent bg-accent text-white hover:bg-accent/80 hover:border-accent/80"
                      }`}
                      data-testid="button-time-flexible"
                    >
                      🎨 My artist can pick the time
                    </button>
                  </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}


          {step === "availability" && (
            <motion.div key="availability" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="max-w-2xl mx-auto">
                <div className="rounded-2xl mb-6 bg-black/80 backdrop-blur-sm border border-white/10 overflow-hidden">
                  <div className="px-6 pt-5 pb-4 border-b border-white/10">
                    <div className="flex items-center gap-2 mb-1">
                      <CalendarIcon size={15} className="text-accent shrink-0" />
                      <span className="font-heading text-sm text-white">When are you generally free?</span>
                    </div>
                    <p className="text-xs text-white/50 leading-relaxed">Pick as many as you like — we'll reach out to confirm a time that works.</p>
                  </div>
                  <div className="px-6 py-6 space-y-7">

                    {/* ── Days of the week ── */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold text-white/70 uppercase tracking-wider">Days of the week</p>
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              const allDays = studio?.softBookingDays ?? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
                              const weekdays = allDays.filter(d => !["Sat", "Sun"].includes(d));
                              setAvailabilityDays(weekdays.length ? weekdays : allDays);
                            }}
                            className="text-xs px-2.5 py-1 rounded-md border border-white/15 text-white/55 hover:border-accent/60 hover:text-accent/80 transition-all"
                          >
                            Weekdays
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const allDays = studio?.softBookingDays ?? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
                              const weekend = allDays.filter(d => ["Sat", "Sun"].includes(d));
                              setAvailabilityDays(weekend.length ? weekend : allDays);
                            }}
                            className="text-xs px-2.5 py-1 rounded-md border border-white/15 text-white/55 hover:border-accent/60 hover:text-accent/80 transition-all"
                          >
                            Weekend
                          </button>
                          <button
                            type="button"
                            onClick={() => setAvailabilityDays(studio?.softBookingDays ?? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"])}
                            className="text-xs px-2.5 py-1 rounded-md border border-white/15 text-white/55 hover:border-accent/60 hover:text-accent/80 transition-all"
                          >
                            All
                          </button>
                          <button
                            type="button"
                            onClick={() => setAvailabilityDays([])}
                            className="text-xs px-2.5 py-1 rounded-md border border-white/15 text-white/55 hover:border-red-400/60 hover:text-red-400/80 transition-all"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-7 gap-1.5">
                        {(studio?.softBookingDays ?? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]).map((day) => (
                          <button
                            key={day}
                            type="button"
                            data-testid={`button-avail-day-${day.toLowerCase()}`}
                            onClick={() => setAvailabilityDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])}
                            className={`py-3 rounded-xl text-xs font-bold text-center transition-all ${
                              availabilityDays.includes(day)
                                ? "bg-accent border-2 border-accent text-white shadow-md shadow-accent/25"
                                : "bg-white/[0.05] border-2 border-white/10 text-white/75 hover:bg-white/10 hover:border-white/25 hover:text-white"
                            }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* ── Times of day — grouped by period ── */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold text-white/70 uppercase tracking-wider">Time of day</p>
                        <button
                          type="button"
                          onClick={() => setAvailabilityTimes(studio?.softBookingTimeSlots ?? ["12pm", "1pm", "2pm", "3pm", "4pm", "5pm", "6pm"])}
                          className="text-xs px-2.5 py-1 rounded-md border border-white/15 text-white/55 hover:border-accent/60 hover:text-accent/80 transition-all"
                        >
                          Any time
                        </button>
                      </div>
                      {(() => {
                        const slots = studio?.softBookingTimeSlots ?? ["12pm", "1pm", "2pm", "3pm", "4pm", "5pm", "6pm"];
                        const morning   = slots.filter(t => parseTimeSlotHour(t) < 12);
                        const afternoon = slots.filter(t => { const h = parseTimeSlotHour(t); return h >= 12 && h < 17; });
                        const evening   = slots.filter(t => parseTimeSlotHour(t) >= 17);
                        const groups = [
                          ...(morning.length   ? [{ label: "Morning",   emoji: "☀️",  times: morning }]   : []),
                          ...(afternoon.length ? [{ label: "Afternoon", emoji: "🌤️", times: afternoon }] : []),
                          ...(evening.length   ? [{ label: "Evening",   emoji: "🌙",  times: evening }]   : []),
                          ...(morning.length === 0 && afternoon.length === 0 && evening.length === 0
                            ? [{ label: "", emoji: "🕐", times: slots }] : []),
                        ];
                        return (
                          <div className="space-y-3.5">
                            {groups.map(({ label, emoji, times }) => (
                              <div key={label || "all"} className="flex items-start gap-3">
                                {label ? (
                                  <div className="flex items-center gap-1.5 w-[88px] shrink-0 pt-1.5">
                                    <span className="text-sm leading-none">{emoji}</span>
                                    <span className="text-xs text-white/50 font-medium">{label}</span>
                                  </div>
                                ) : null}
                                <div className="flex flex-wrap gap-2 flex-1">
                                  {times.map((t) => (
                                    <button
                                      key={t}
                                      type="button"
                                      data-testid={`button-avail-time-${t.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                                      onClick={() => setAvailabilityTimes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])}
                                      className={`px-3.5 py-2 rounded-lg text-sm font-semibold border-2 transition-all min-h-[38px] ${
                                        availabilityTimes.includes(t)
                                          ? "border-accent bg-accent text-white shadow-md shadow-accent/25"
                                          : "border-white/12 bg-white/[0.05] text-white/80 hover:border-white/28 hover:text-white hover:bg-white/10"
                                      }`}
                                    >
                                      {formatTimeSlot(t)}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>

                    {/* ── Validation hint ── */}
                    {(availabilityDays.length === 0 || availabilityTimes.length === 0) && (
                      <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/8">
                        <Info size={13} className="text-white/35 shrink-0" />
                        <p className="text-xs text-white/45">Select at least one day and one time to continue.</p>
                      </div>
                    )}

                    <button
                      type="button"
                      data-testid="button-availability-continue"
                      disabled={availabilityDays.length === 0 || availabilityTimes.length === 0}
                      onClick={() => {
                        if (returnToStep) {
                          setStep(returnToStep);
                          setReturnToStep(null);
                          return;
                        }
                        if (skipPreFormEstimate) {
                          setStep("body-map");
                        } else {
                          setEstSubStep(0);
                          setEstShowResults(false);
                          setEstResetKey(k => k + 1);
                          setEstimateStatus("completed");
                          setStep("estimate");
                        }
                      }}
                      className="w-full py-3.5 rounded-xl bg-accent hover:bg-accent/80 text-white font-semibold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Continue →
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          <FeatureFlagZone flagKey="piercings_enabled">
            {step === "estimate" && serviceCategory === "piercing" && (
            <motion.div key="estimate-piercing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="max-w-2xl mx-auto">
                <div className="rounded-2xl mb-6 bg-black/80 backdrop-blur-sm border border-white/10 overflow-hidden">
                  <div className="px-6 pt-5 pb-4 border-b border-white/10">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign size={15} className="text-accent shrink-0" />
                      <span className="font-heading text-sm text-white">Piercing Price Estimate</span>
                    </div>
                    <p className="text-xs text-white/40 leading-relaxed">
                      For personal planning only — not a quote from the studio. Your piercer will confirm pricing.
                    </p>
                  </div>
                  <div className="p-6 space-y-5">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-subheading">
                        Piercing Type *
                      </label>
                      <select
                        value={piercingType}
                        onChange={(e) => setPiercingType(e.target.value)}
                        className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-accent focus:outline-none transition-colors"
                        data-testid="select-piercing-type"
                      >
                        <option value="">Select piercing type…</option>
                        {(piercingPricingData.length > 0 ? piercingPricingData : []).map((pt) => (
                          <option key={pt.piercingType} value={pt.piercingType}>{pt.displayName}</option>
                        ))}
                        <option value="other">Other / Not listed</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-subheading">
                        Jewelry Material
                      </label>
                      <select
                        value={piercingMaterial}
                        onChange={(e) => setPiercingMaterial(e.target.value)}
                        className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-accent focus:outline-none transition-colors"
                        data-testid="select-piercing-material"
                      >
                        {JEWELRY_MATERIALS.map((m) => (
                          <option key={m.id} value={m.id}>{m.label} — {m.description}</option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-subheading">
                          Gauge
                        </label>
                        <select
                          value={piercingGauge}
                          onChange={(e) => setPiercingGauge(e.target.value)}
                          className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-accent focus:outline-none transition-colors"
                          data-testid="select-piercing-gauge"
                        >
                          {JEWELRY_GAUGES.map((g) => (
                            <option key={g.id} value={g.id}>{g.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-subheading">
                          Count
                        </label>
                        <select
                          value={piercingCount}
                          onChange={(e) => setPiercingCount(parseInt(e.target.value, 10))}
                          className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-accent focus:outline-none transition-colors"
                          data-testid="select-piercing-count"
                        >
                          {[1,2,3,4,5,6,7,8].map((n) => (
                            <option key={n} value={n}>{n} {n === 1 ? "piercing" : "piercings"}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {piercingEstimate && (
                      <div className="rounded-xl p-4 bg-accent/10 border border-accent/20 mt-2">
                        <p className="text-xs uppercase tracking-widest text-violet-300 font-subheading font-bold mb-1">Estimated Range</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-heading text-white" data-testid="text-piercing-estimate-low">${piercingEstimate.low}</span>
                          <span className="text-lg text-white/50 font-heading">–</span>
                          <span className="text-3xl font-heading text-white" data-testid="text-piercing-estimate-high">${piercingEstimate.high}</span>
                        </div>
                        <p className="text-xs text-white/50 mt-1">For {piercingCount} {piercingCount === 1 ? "piercing" : "piercings"} · jewelry included</p>
                      </div>
                    )}
                    {!piercingEstimate && piercingType && (
                      <div className="rounded-xl p-4 bg-white/5 border border-white/10 text-center">
                        <p className="text-xs text-white/40">Select a piercing type to see a price estimate</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setStep(isSoftBooking ? "availability" : "time")}
                    className="inline-flex items-center uppercase tracking-widest text-xs gap-2 group px-6 py-3 rounded-full transition-all text-white bg-black/80 border border-white/10 hover:bg-accent hover:border-accent active:scale-[0.97] active:brightness-90 cursor-pointer font-bold font-subheading"
                    data-testid="button-piercing-est-back"
                  >
                    <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Back
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => { setEstimateStatus("completed"); if (returnToStep) { setStep(returnToStep); setReturnToStep(null); } else { setStep("notes"); } }}
                      className="btn-cta-glow inline-flex items-center justify-center bg-accent text-white uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 transition-colors gap-2 px-6 py-3 rounded-full"
                      data-testid="button-piercing-est-continue"
                    >
                      Continue <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          </FeatureFlagZone>

          <FeatureFlagZone flagKey="ab_estimate_enabled">
          {step === "estimate" && serviceCategory !== "piercing" && estimateVariant === "C" && (
            <motion.div key="estimate-c" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="max-w-2xl mx-auto">
                <div className="rounded-2xl mb-6 bg-black/80 backdrop-blur-sm border border-white/10 overflow-hidden">
                  <div className="px-6 pt-5 pb-4 border-b border-white/10">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign size={15} className="text-accent shrink-0" />
                      <span className="font-heading text-sm text-white">Quick Price Estimate</span>
                    </div>
                    <p className="text-xs text-white/40 leading-relaxed">
                      For personal planning only — not a quote from the studio.
                    </p>
                  </div>
                  <div className="p-6 md:p-8">
                    <h2 className="text-2xl md:text-3xl font-heading text-white tracking-wide mb-2" data-testid="text-estimate-c-question">
                      What's your rough budget?
                    </h2>
                    <p className="text-muted-foreground font-light mb-8">
                      Pick the range that feels right — your artist will confirm the actual price during consultation.
                    </p>
                    <div className="space-y-3">
                      {PRICE_RANGE_BUCKETS.map((bucket) => {
                        const isAnimating = variantCAnimating === bucket.id;
                        const isSelected = variantCBucket === bucket.id && variantCAnimating === null;
                        return (
                          <button
                            key={bucket.id}
                            onClick={() => variantCAnimating === null ? handleVariantCSelect(bucket.id) : undefined}
                            disabled={variantCAnimating !== null}
                            className={`w-full text-left border rounded-lg p-5 transition-all group bg-black/60 ${
                              isAnimating
                                ? "border-accent bg-accent/20 text-white scale-[0.98]"
                                : isSelected
                                ? "border-accent/40 bg-accent/10 text-white"
                                : variantCAnimating !== null
                                ? "border-white/5 text-white/40 opacity-60"
                                : "border-white/10 text-white/80 hover:text-white hover:border-accent/50 hover:bg-accent/5 active:bg-accent/20 active:border-accent active:text-white active:scale-[0.98]"
                            }`}
                            data-testid={`button-est-c-${bucket.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-subheading font-semibold text-sm tracking-wide block">{bucket.label}</span>
                                <span className="text-xs text-white/60 mt-1 block">{bucket.description}</span>
                              </div>
                              {isAnimating ? (
                                <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "#8b5cf6" }}>
                                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                </span>
                              ) : (
                                <ArrowRight size={16} className="text-muted-foreground group-hover:text-accent transition-colors opacity-0 group-hover:opacity-100 shrink-0" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setStep(isSoftBooking ? "availability" : "time")}
                    className="inline-flex items-center uppercase tracking-widest text-xs gap-2 group px-6 py-3 rounded-full transition-all text-white bg-black/80 border border-white/10 hover:bg-accent hover:border-accent active:scale-[0.97] active:brightness-90 cursor-pointer font-bold font-subheading"
                    data-testid="button-est-c-back"
                  >
                    <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Back
                  </button>
                  <button
                    onClick={() => { setEstimateStatus("completed"); if (returnToStep) { setStep(returnToStep); setReturnToStep(null); } else { setStep("body-map"); } }}
                    className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-medium transition-all px-4 py-2.5 rounded-full border border-white/15 hover:border-white/30 bg-white/5 hover:bg-white/8 min-h-[44px]"
                    data-testid="button-est-c-skip"
                  >
                    Skip for now
                  </button>
                </div>
              </div>
            </motion.div>
          )}
          </FeatureFlagZone>

          <FeatureFlagZone flagKey="ab_estimate_enabled">
          {step === "estimate" && serviceCategory !== "piercing" && (estimateVariant === "A" || estimateVariant === "B") && (
            <motion.div key="estimate" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="max-w-3xl mx-auto">
                <AnimatePresence mode="wait">
                  {!estShowResults ? (
                    <motion.div
                      key={`est-${estSubStep}`}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.3 }}
                    >
                      {(() => {
                        const currentSubStep = ESTIMATE_SUB_STEPS[estSubStep];
                        const stepInfo = getEstSubStepTitle(currentSubStep);
                        const options = getEstSubStepOptions(currentSubStep);
                        const currentValue = getEstValue(currentSubStep);

                        return (
                          <div className="rounded-2xl mb-6 bg-black/80 backdrop-blur-sm border border-white/10 overflow-hidden">
                            {/* Unified header: title + counter + context + progress bar */}
                            <div className="px-6 pt-5 pb-4 border-b border-white/10">
                              <div className="flex items-center justify-between gap-3 mb-2">
                                <div className="flex items-center gap-2">
                                  <DollarSign size={15} className="text-accent shrink-0" />
                                  <span className="font-heading text-sm text-white">Quick Price Estimate</span>
                                </div>
                                <span className="text-xs font-subheading text-white/40 uppercase tracking-widest shrink-0">
                                  Q{estSubStep + 1} of {ESTIMATE_SUB_STEPS.length}
                                </span>
                              </div>
                              <p className="text-xs text-white/40 leading-relaxed mb-3">
                                Still part of your request with <span className="text-white/60">{selectedArtistData?.name}</span> on <span className="text-white/60">{selectedDateFormatted}</span> — for personal planning only.
                              </p>
                              <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                                <motion.div
                                  key={estResetKey}
                                  className="h-full bg-accent rounded-full"
                                  initial={{ width: "0%" }}
                                  animate={{ width: `${estProgress}%` }}
                                  transition={{ duration: 0.4, ease: "easeOut" }}
                                />
                              </div>
                            </div>
                            {/* Question body */}
                            <div className="p-6 md:p-8">
                              <h2 className="text-2xl md:text-3xl font-heading text-white tracking-wide mb-2" data-testid="text-estimate-question">
                                {stepInfo.title}
                              </h2>
                              <p className="text-muted-foreground font-light mb-8">
                                {stepInfo.subtitle}
                              </p>
                              <div className="space-y-3">
                                {options.map((option) => {
                                  const isAnimating = estAnimating === option.id;
                                  const isPreviouslySelected = currentValue === option.id && estAnimating === null;
                                  const optionIcon = STEP_ICON_MAP[currentSubStep]?.[option.id];
                                  const optionColor = STEP_COLOR_MAP[currentSubStep]?.[option.id];
                                  return (
                                    <button
                                      key={option.id}
                                      onClick={() => estAnimating === null ? handleEstSelect(option.id) : undefined}
                                      disabled={estAnimating !== null}
                                      className={`w-full text-left border rounded-lg p-5 transition-all group bg-black/60 ${
                                        isAnimating
                                          ? "border-accent bg-accent/20 text-white scale-[0.98]"
                                          : isPreviouslySelected
                                          ? "border-accent/40 bg-accent/10 text-white"
                                          : estAnimating !== null
                                          ? "border-white/5 text-white/40 opacity-60"
                                          : "border-white/10 text-white/80 hover:text-white hover:border-accent/50 hover:bg-accent/5 active:bg-accent/20 active:border-accent active:text-white active:scale-[0.98]"
                                      }`}
                                      style={{
                                        ...(isAnimating && optionColor ? { borderColor: optionColor } : {}),
                                      }}
                                      data-testid={`button-est-${option.id}`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                          {optionIcon && (
                                            <span
                                              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-colors"
                                              style={{
                                                color: optionColor,
                                                backgroundColor: `${optionColor}15`,
                                                border: `1px solid ${optionColor}30`,
                                              }}
                                            >
                                              {optionIcon}
                                            </span>
                                          )}
                                          <div>
                                            <span className="font-subheading font-semibold text-sm tracking-wide block">{option.label}</span>
                                            <span className="text-xs text-white/60 mt-1 block">{option.description}</span>
                                          </div>
                                        </div>
                                        {isAnimating ? (
                                          <span
                                            className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                                            style={{ backgroundColor: optionColor || '#8b5cf6' }}
                                          >
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                          </span>
                                        ) : (
                                          <ArrowRight size={16} className="text-muted-foreground group-hover:text-accent transition-colors opacity-0 group-hover:opacity-100 shrink-0" />
                                        )}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      <div className="flex items-center justify-between">
                        <button
                          onClick={estGoBack}
                          className="inline-flex items-center uppercase tracking-widest text-xs gap-2 group px-6 py-3 rounded-full transition-all text-white bg-black/80 border border-white/10 hover:bg-accent hover:border-accent active:scale-[0.97] active:brightness-90 cursor-pointer font-bold font-subheading"
                          data-testid="button-est-back"
                        >
                          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Back
                        </button>
                        <div className="flex items-center gap-3">
                          {estimateVariant === "B" && (
                            <button
                              onClick={handleEstimateSkip}
                              className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-medium transition-all px-4 py-2.5 rounded-full border border-white/15 hover:border-white/30 bg-white/5 hover:bg-white/8 min-h-[44px]"
                              data-testid="button-est-b-skip"
                            >
                              Skip — I'll discuss pricing with my artist
                            </button>
                          )}
                          <button
                            onClick={estRestart}
                            className="inline-flex items-center text-white bg-black/80 border border-white/10 hover:bg-accent hover:border-accent transition-all uppercase tracking-widest text-xs font-bold font-subheading gap-2 px-6 py-3 rounded-full"
                            data-testid="button-est-restart"
                          >
                            <RotateCcw size={14} /> Start Over
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ) : estimate ? (
                    <motion.div
                      key="est-results"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                    >
                      <div className="rounded-2xl p-8 md:p-10 mb-6 bg-black/80 backdrop-blur-sm border border-white/10">
                                        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
                          <div className="flex items-center gap-3">
                            <DollarSign size={20} className="text-accent" />
                            <h2 className="text-violet-300 uppercase tracking-[0.3em] text-xs font-bold font-subheading">
                              Your Estimate
                            </h2>
                          </div>
                          <div className="flex items-center gap-3 flex-wrap">
                            <button
                              onClick={estRestart}
                              className="inline-flex items-center justify-center bg-black/60 border border-white/20 text-white/80 hover:bg-accent hover:border-accent hover:text-white transition-all uppercase tracking-widest text-xs font-bold font-subheading gap-2 px-5 py-2.5 rounded-full min-h-[44px]"
                              data-testid="button-est-redo-top"
                            >
                              <RotateCcw size={13} /> Redo
                            </button>
                            <button
                              onClick={() => { if (returnToStep) { setStep(returnToStep); setReturnToStep(null); } else { setStep("body-map"); } }}
                              className="btn-cta-glow inline-flex items-center justify-center bg-accent text-white uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 transition-colors gap-2 px-5 py-2.5 rounded-full min-h-[44px]"
                              data-testid="button-continue-to-details-top"
                            >
                              Continue <ArrowRight size={13} />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-5xl md:text-6xl font-heading text-white" data-testid="text-estimate-low">
                            ${estimate.low}
                          </span>
                          <span className="text-2xl text-white/50 mx-2 font-heading">–</span>
                          <span className="text-5xl md:text-6xl font-heading text-white" data-testid="text-estimate-high">
                            ${estimate.high}
                          </span>
                        </div>
                        {estimate.low === 60 && (
                          <p className="text-xs text-white/60 italic mb-4">Shop minimum: $60</p>
                        )}
                        <div className="flex gap-8 mt-4 mb-8">
                          <div>
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-violet-300 font-subheading font-bold mb-1">
                              <Clock size={12} /> Est. Time
                            </div>
                            <p className="text-xl font-heading text-white">
                              {estimate.hours < 1 ? `${Math.round(estimate.hours * 60)} min` : `${estimate.hours} hrs`}
                            </p>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-violet-300 font-subheading font-bold mb-1">
                              <Calendar size={12} /> Sessions
                            </div>
                            <p className="text-xl font-heading text-white">
                              {estimate.sessions}
                            </p>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-violet-300 font-subheading font-bold mb-1">
                              <DollarSign size={12} /> Payment
                            </div>
                            <p className="text-xl font-heading text-white" data-testid="text-payment-info">
                              At the shop
                            </p>
                          </div>
                        </div>

                        <TipGuide low={estimate.low} high={estimate.high} />

                        <div className="border-t border-white/10 pt-6 mb-6 mt-6">
                          <div className="flex items-start gap-3 rounded-xl p-4 bg-amber-500/10 border border-amber-500/20">
                            <AlertCircle size={16} className="shrink-0 mt-0.5 text-amber-400" />
                            <div>
                              <p className="text-sm font-medium text-amber-200 mb-2">
                                For Personal Budgeting Only
                              </p>
                              <p className="text-sm leading-relaxed text-white/70">
                                This estimate is a projection to help you ballpark a budget and time commitment.
                                The shop does not reference or use this pricing — actual cost and session time
                                are determined by your artist during consultation and may differ significantly.
                                All payment takes place at the shop — we accept cash and card.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-start gap-3 rounded-xl px-4 py-3 bg-amber-500/10 border border-amber-500/30 mb-4">
                          <AlertCircle size={16} className="shrink-0 mt-0.5 text-amber-400" />
                          <p className="text-sm text-amber-200 leading-snug">
                            <span className="font-semibold">Your booking isn't complete yet.</span>{" "}
                            This is only an estimate — click <span className="font-semibold">Continue</span> to finish submitting your request.
                          </p>
                        </div>
                        <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
                          <button
                            onClick={estRestart}
                            className="inline-flex items-center justify-center bg-black/60 border border-white/20 text-white/80 hover:bg-accent hover:border-accent hover:text-white transition-all uppercase tracking-widest text-xs font-bold font-subheading gap-2 w-full sm:w-auto px-8 py-4 rounded-full"
                            data-testid="button-est-redo"
                          >
                            <RotateCcw size={14} /> Redo Estimate
                          </button>
                          <button
                            onClick={() => { if (returnToStep) { setStep(returnToStep); setReturnToStep(null); } else { setStep("body-map"); } }}
                            className="btn-cta-glow inline-flex items-center justify-center bg-accent text-white uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 gap-2 w-full sm:w-auto px-8 py-4 rounded-full"
                            data-testid="button-continue-to-details"
                          >
                            Continue <ArrowRight size={14} />
                          </button>
                        </div>

                        <div className="border-t border-white/10 pt-4 mt-4">
                          <p className="text-[10px] leading-relaxed text-white/60" data-testid="text-estimate-disclaimer">
                            <span className="font-bold text-white/50 uppercase tracking-wider">Disclaimer:</span>{" "}
                            This pricing and time estimate is generated by an automated tool for informational purposes only
                            and does not constitute a quote, offer, or guarantee of any kind. {studio.name},
                            its artists, and staff are not bound by these projections and bear no responsibility for any
                            expectations formed from using this tool. Final pricing, session duration, and number of sessions
                            are determined solely by your artist based on the actual design, placement, and complexity discussed
                            during your consultation. By proceeding, you acknowledge that this estimate is non-binding and
                            intended only as a personal budgeting aid.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
          </FeatureFlagZone>

          {step === "body-map" && (
            <motion.div key="body-map" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="max-w-xl mx-auto">
                <div className="rounded-2xl p-6 md:p-10 bg-black/80 backdrop-blur-sm border border-white/10">
                  {(() => {
                    const bmCopy = getStepCopy(serviceCategory, "bodyMap");
                    return (
                      <BodyMapStep
                        value={bodyMap}
                        onChange={setBodyMap}
                        onNext={() => setStep(isFlashBooking ? "form" : (isWalkin ? "notes" : "images"))}
                        onPhaseChange={(ph) => trackSubFunnelEvent(`body-map:${ph}`)}
                        heading={bmCopy.heading}
                        sizeQuestion={bmCopy.sizeQuestion}
                        pinPromptOverlay={bmCopy.prompt}
                        toastNoPinCopy={bmCopy.toastNoPin}
                        serviceType={serviceCategory || undefined}
                      />
                    );
                  })()}
                </div>
              </div>
            </motion.div>
          )}

          {step === "form" && (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="md:col-span-2 rounded-2xl p-8 bg-black/80 backdrop-blur-sm border border-white/10">
                  <h2 className="text-xl font-heading tracking-wide text-white mb-6">Your Details</h2>
                  <form onSubmit={handleFormContinue} className="space-y-5">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-subheading">
                        <User size={12} className="inline mr-2" />Name *
                      </label>
                      <input
                        type="text"
                        required
                        name="name"
                        autoComplete="name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:border-accent focus:outline-none transition-colors"
                        placeholder="Your full name"
                        data-testid="input-booking-name"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-subheading">
                          <Mail size={12} className="inline mr-2" />Email *
                        </label>
                        <input
                          type="email"
                          required
                          name="email"
                          autoComplete="email"
                          value={form.email}
                          onChange={(e) => { setBookingEmailTouched(true); setForm({ ...form, email: e.target.value }); }}
                          onBlur={() => setBookingEmailTouched(true)}
                          className={`w-full bg-black/50 border rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none transition-colors ${bookingEmailTouched && form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ? "border-red-500/60 focus:border-red-500/80" : "border-white/10 focus:border-accent"}`}
                          placeholder="your@email.com"
                          data-testid="input-booking-email"
                        />
                        {bookingEmailTouched && form.email && !form.email.includes("@") && (
                          <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1" data-testid="text-booking-email-missing-at">
                            <span>⚠</span> An email address must include an '@' — e.g. you@example.com
                          </p>
                        )}
                        {bookingEmailTouched && form.email && form.email.includes("@") && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && (
                          <p className="mt-1.5 text-xs text-amber-400/90 flex items-center gap-1" data-testid="text-booking-email-invalid">
                            <span>⚠</span> Please enter a complete email address — e.g. you@example.com
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-subheading">
                          <Phone size={12} className="inline mr-2" />Phone
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          autoComplete="tel"
                          value={form.phone}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                            let formatted = digits;
                            if (digits.length > 6) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                            else if (digits.length > 3) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                            else if (digits.length > 0) formatted = `(${digits}`;
                            setForm({ ...form, phone: formatted });
                            if (phoneError) setPhoneError(validatePhone(formatted));
                          }}
                          onBlur={(e) => setPhoneError(validatePhone(e.target.value))}
                          className={`w-full bg-black/50 border rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none transition-colors ${phoneError ? "border-red-500 focus:border-red-400" : "border-white/10 focus:border-accent"}`}
                          placeholder="(555) 123-4567"
                          data-testid="input-booking-phone"
                          aria-invalid={!!phoneError}
                          aria-describedby={phoneError ? "phone-error" : undefined}
                        />
                        {phoneError && (
                          <p id="phone-error" className="mt-1.5 text-xs text-red-400 flex items-center gap-1" data-testid="error-booking-phone" role="alert">
                            <AlertCircle size={11} className="shrink-0" />{phoneError}
                          </p>
                        )}
                      </div>
                    </div>
                    {serviceCategory === "piercing" && (
                      <div className="rounded-xl border border-accent/20 bg-accent/5 p-4 space-y-4">
                        <p className="text-xs uppercase tracking-widest text-violet-300 font-subheading font-bold">Piercing Details</p>
                        <div>
                          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-subheading">
                            Piercing Type
                          </label>
                          <select
                            value={piercingType}
                            onChange={(e) => setPiercingType(e.target.value)}
                            className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-accent focus:outline-none transition-colors"
                            data-testid="select-form-piercing-type"
                          >
                            <option value="">Not specified</option>
                            {(piercingPricingData.length > 0 ? piercingPricingData : []).map((pt) => (
                              <option key={pt.piercingType} value={pt.piercingType}>{pt.displayName}</option>
                            ))}
                            <option value="other">Other / Not listed</option>
                          </select>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-subheading">
                              Jewelry Material
                            </label>
                            <select
                              value={piercingMaterial}
                              onChange={(e) => setPiercingMaterial(e.target.value)}
                              className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-accent focus:outline-none transition-colors"
                              data-testid="select-form-piercing-material"
                            >
                              {JEWELRY_MATERIALS.map((m) => (
                                <option key={m.id} value={m.id}>{m.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-subheading">
                              Count
                            </label>
                            <select
                              value={piercingCount}
                              onChange={(e) => setPiercingCount(parseInt(e.target.value, 10))}
                              className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-accent focus:outline-none transition-colors"
                              data-testid="select-form-piercing-count"
                            >
                              {[1,2,3,4,5,6,7,8].map((n) => (
                                <option key={n} value={n}>{n} {n === 1 ? "piercing" : "piercings"}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-subheading">
                              Gauge
                            </label>
                            <select
                              value={piercingGauge}
                              onChange={(e) => setPiercingGauge(e.target.value)}
                              className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-accent focus:outline-none transition-colors"
                              data-testid="select-form-piercing-gauge"
                            >
                              {JEWELRY_GAUGES.map((g) => (
                                <option key={g.id} value={g.id}>{g.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 font-subheading">
                              Jewelry Style
                            </label>
                            <select
                              value={piercingStyle}
                              onChange={(e) => setPiercingStyle(e.target.value)}
                              className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-accent focus:outline-none transition-colors"
                              data-testid="select-form-piercing-style"
                            >
                              {JEWELRY_STYLES.map((s) => (
                                <option key={s.id} value={s.id}>{s.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {piercingEstimate && (
                          <div className="flex items-center gap-3 rounded-lg p-3 bg-accent/10 border border-accent/20">
                            <DollarSign size={14} className="text-accent shrink-0" />
                            <div>
                              <p className="text-xs text-violet-300 font-subheading font-bold uppercase tracking-widest">Estimate</p>
                              <p className="text-sm text-white">${piercingEstimate.low} – ${piercingEstimate.high}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* FUTURE DATA CAPTURE (do not add yet — document only)
                      These fields were intentionally removed from the release form because it
                      adds friction at the wrong moment. The booking contact step (here) is the
                      right place to collect them once ready:

                      1. Referral source ("How did you hear about us?")
                         — dropdown: Instagram / Google / Friend / Returning client / Other
                         — store on the booking record; feeds attribution analytics

                      2. Social media handle (optional)
                         — single text input, e.g. "@handle"
                         — useful for tagging clients in healed-photo posts

                      3. Newsletter opt-in already exists below — leave it here.
                         When referral source is added, place it directly above the newsletter
                         checkbox so the optional extras group together at the bottom of the form.
                    */}
                    {isPublic && (
                      <label className="flex items-start gap-3 cursor-pointer group" data-testid="label-booking-newsletter-optin">
                        <input
                          type="checkbox"
                          checked={form.subscribeToNewsletter}
                          onChange={(e) => setForm({ ...form, subscribeToNewsletter: e.target.checked })}
                          className="mt-0.5 w-4 h-4 rounded border-white/20 bg-black/50 text-accent focus:ring-accent/50 cursor-pointer accent-[#8b5cf6]"
                          data-testid="checkbox-booking-newsletter-optin"
                        />
                        <span className="text-sm text-white/60 group-hover:text-white/80 transition-colors leading-snug">
                          Sign me up for the {studio.shortName} mailing list — flash sales, events, and studio news. You'll get a confirmation email to verify.
                        </span>
                      </label>
                    )}

                    {isPublic && (
                      <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken("")} className="flex justify-center" />
                    )}

                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <button
                        type="button"
                        onClick={() => setStep(isFlashBooking ? "body-map" : "notes")}
                        className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 rounded-full bg-accent/20 border border-accent/30 text-white uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent hover:border-accent active:scale-[0.97] active:brightness-90 transition-all group"
                        data-testid="button-form-back"
                      >
                        <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Back
                      </button>
                      <button
                        type="submit"
                        disabled={bookingMutation.isPending || imageUploading}
                        className="inline-flex items-center justify-center gap-2 w-full sm:flex-1 py-4 rounded-full bg-accent text-white uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 active:scale-[0.97] active:bg-accent/70 transition-all duration-300 shadow-[0_0_15px_rgba(139,92,246,0.4)] hover:shadow-[0_0_25px_rgba(139,92,246,0.6)] disabled:opacity-50"
                        data-testid="button-confirm-booking"
                        onMouseEnter={prefetchAftercare}
                        onFocus={prefetchAftercare}
                      >
                        {imageUploading ? "Uploading image..." : bookingMutation.isPending ? "Submitting..." : isAdmin ? "Add Appointment" : isWalkin ? "Create Walk-In & Confirm" : "Submit Request"}
                      </button>
                    </div>
                    {isPublic && (
                      <div className="mt-4 space-y-1.5">
                        <p className="text-[10px] text-white/55 text-center">
                          By submitting, you're requesting an appointment — not confirming one. Your artist will contact you to finalize.
                        </p>
                        {(studio?.cancellationWindowHours ?? 0) > 0 && (
                          <p className="text-[10px] text-amber-400/50 text-center" data-testid="text-cancellation-policy">
                            Deposits are non-refundable if cancelled within{" "}
                            {(studio?.cancellationWindowHours ?? 48) >= 24
                              ? `${Math.round((studio?.cancellationWindowHours ?? 48) / 24)} day(s)`
                              : `${studio?.cancellationWindowHours} hour(s)`}{" "}
                            of your appointment or in the event of a no-show.
                          </p>
                        )}
                      </div>
                    )}
                    {isAdmin && (
                      <p className="text-[10px] text-blue-400/50 text-center mt-4">
                        Admin entries bypass the daily limit. The booking is immediately confirmed. All entries are logged.
                      </p>
                    )}
                    {isWalkin && (
                      <p className="text-[10px] text-purple-400/50 text-center mt-4">
                        This will create a confirmed booking immediately. Duplicate and overbooking warnings will appear if applicable.
                      </p>
                    )}
                  </form>
                </div>

                <div className="md:col-span-1">
                  <div className="rounded-2xl p-6 bg-black/80 backdrop-blur-sm border border-white/10 sticky top-24">
                    <h3 className="font-heading text-lg text-white mb-4">Request Summary</h3>
                    <div className="space-y-4 text-sm">
                      <div className="flex items-start gap-3">
                        <User size={16} className="text-accent shrink-0 mt-0.5" />
                        <div>
                          <p className="text-white/50 text-xs uppercase tracking-wider mb-0.5">Artist</p>
                          <p className="text-white">{selectedArtistData?.name}</p>
                        </div>
                      </div>
                      {isSoftBooking ? (
                        <div className="flex items-start gap-3">
                          <CalendarIcon size={16} className="text-accent shrink-0 mt-0.5" />
                          <div>
                            <p className="text-white/50 text-xs uppercase tracking-wider mb-0.5">Availability</p>
                            <p className="text-white text-xs leading-relaxed">
                              {availabilityDays.length > 0 ? `Days: ${availabilityDays.join(", ")}` : "Days: Any"}<br/>
                              {availabilityTimes.length > 0 ? `Times: ${availabilityTimes.join(", ")}` : "Times: Any"}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start gap-3">
                            <Calendar size={16} className="text-accent shrink-0 mt-0.5" />
                            <div>
                              <p className="text-white/50 text-xs uppercase tracking-wider mb-0.5">Date</p>
                              <p className="text-white">{selectedDateFormatted}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <Clock size={16} className="text-accent shrink-0 mt-0.5" />
                            <div>
                              <p className="text-white/50 text-xs uppercase tracking-wider mb-0.5">Preferred Time</p>
                              <p className="text-white">{formatTimePref(selectedTime)}</p>
                            </div>
                          </div>
                        </>
                      )}
                      {isFlashBooking && (
                        <div className="flex items-start gap-3">
                          <Sparkles size={16} className="text-accent shrink-0 mt-0.5" />
                          <div>
                            <p className="text-white/50 text-xs uppercase tracking-wider mb-0.5">Flash Design</p>
                            <p className="text-white">{urlFlash}</p>
                            {urlFlashPrice != null ? (
                              <p className="text-violet-300 text-xs mt-0.5">${urlFlashPrice.toFixed(0)}</p>
                            ) : (
                              <p className="text-white/50 text-xs mt-0.5">Price confirmed with your artist</p>
                            )}
                          </div>
                        </div>
                      )}
                      {estimate && !isFlashBooking && (
                        <div className="flex items-start gap-3">
                          <DollarSign size={16} className="text-accent shrink-0 mt-0.5" />
                          <div>
                            <p className="text-white/50 text-xs uppercase tracking-wider mb-0.5">Estimate</p>
                            <p className="text-white">${estimate.low} – ${estimate.high}</p>
                            <p className="text-white/60 text-[10px] mt-1 leading-relaxed">For budgeting only — not a quote. Final price set by artist.</p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mt-6 pt-4 border-t border-white/10 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-white/40 uppercase tracking-widest font-subheading shrink-0">Change:</span>
                      {isSoftBooking ? (
                        <button onClick={() => { setReturnToStep("form"); setStep("availability"); }} className="inline-flex items-center gap-1.5 bg-accent/10 border border-accent/20 text-violet-300 hover:bg-accent hover:text-white hover:border-accent transition-colors uppercase tracking-widest text-xs font-bold font-subheading px-4 py-2 rounded-full" data-testid="button-change-availability">
                          <CalendarIcon size={11} /> Availability
                        </button>
                      ) : (
                        <>
                          <button onClick={() => { setReturnToStep("form"); setStep("calendar"); }} className="inline-flex items-center gap-1.5 bg-accent/10 border border-accent/20 text-violet-300 hover:bg-accent hover:text-white hover:border-accent transition-colors uppercase tracking-widest text-xs font-bold font-subheading px-4 py-2 rounded-full" data-testid="button-change-date">
                            <Calendar size={11} /> Date
                          </button>
                          <button onClick={() => { setReturnToStep("form"); setStep("time"); }} className="inline-flex items-center gap-1.5 bg-accent/10 border border-accent/20 text-violet-300 hover:bg-accent hover:text-white hover:border-accent transition-colors uppercase tracking-widest text-xs font-bold font-subheading px-4 py-2 rounded-full" data-testid="button-change-time">
                            <Clock size={11} /> Time
                          </button>
                        </>
                      )}
                      {!isFlashBooking && !skipPreFormEstimate && (
                        <button onClick={() => { setReturnToStep("form"); setStep("estimate"); setEstShowResults(false); setEstSubStep(0); }} className="inline-flex items-center gap-1.5 bg-accent/10 border border-accent/20 text-violet-300 hover:bg-accent hover:text-white hover:border-accent transition-colors uppercase tracking-widest text-xs font-bold font-subheading px-4 py-2 rounded-full" data-testid="button-change-estimate">
                          Estimate
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {step === "images" && (
            <motion.div key="images" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="max-w-2xl mx-auto rounded-2xl p-8 md:p-10 bg-black/80 backdrop-blur-sm border border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <ImageIcon size={20} className="text-accent" />
                  <h2 className="text-xl font-heading tracking-wide text-white">Reference Images</h2>
                </div>
                <p className="text-sm text-white/50 mb-6">
                  Upload up to {MAX_REFERENCE_IMAGES} images of designs, styles, or placement ideas to help your artist understand your vision. Even a rough screenshot or saved photo helps.
                </p>

                {referenceImages.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3" data-testid="reference-images-grid">
                    {referenceImages.map((img, idx) => (
                      <div key={idx} className="relative group aspect-square">
                        <img
                          src={img.preview}
                          alt={`Reference ${idx + 1}`}
                          className="w-full h-full rounded-lg border border-white/10 object-cover"
                          data-testid={`img-reference-preview-${idx}`}
                        />
                        <button
                          onClick={() => removeImage(idx)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-400 transition-colors opacity-0 group-hover:opacity-100"
                          data-testid={`button-remove-reference-${idx}`}
                          type="button"
                          aria-label="Remove image"
                        >
                          <X size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {referenceImages.length < MAX_REFERENCE_IMAGES && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-3 w-full px-4 py-3 rounded-lg border border-dashed border-white/20 bg-white/5 hover:border-accent/50 hover:bg-accent/5 transition-all text-sm text-white/50 hover:text-white/70 mb-6"
                    data-testid="button-upload-reference"
                    type="button"
                  >
                    <Upload size={18} className="text-accent" />
                    <span>{referenceImages.length === 0 ? "Click to upload reference images" : "Add more images"}</span>
                    <span className="ml-auto text-xs text-white/55">Up to {MAX_REFERENCE_IMAGES}, max 5 MB each</span>
                  </button>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                  data-testid="input-reference-file"
                />

                <FeatureFlagZone flagKey="ai_sketch_generator">
                {isFlagEnabled("ai_sketch_generator") && (
                  <AiSketchGenerator
                    existingDescription={form.description || undefined}
                    onAddToReferences={(file) => {
                      const preview = URL.createObjectURL(file);
                      setReferenceImages((prev) => [...prev, { file, preview }]);
                    }}
                  />
                )}
                </FeatureFlagZone>

                <div className="flex flex-col sm:flex-row items-center gap-4 mt-6">
                  <button
                    onClick={() => setStep("body-map")}
                    className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 rounded-full bg-accent/20 border border-accent/30 text-white uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent hover:border-accent active:scale-[0.97] active:brightness-90 transition-all group"
                    data-testid="button-images-back"
                    type="button"
                  >
                    <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Back
                  </button>
                  <button
                    onClick={async () => {
                      if (referenceImages.length === 0) {
                        setShowImagesSkipConfirm(true);
                        return;
                      }
                      // Upload file images eagerly here so submit is never blocked by an upload
                      if (referenceImages.length > 0 && referenceImageUrls.length === 0) {
                        setImageUploading(true);
                        try {
                          const formData = new FormData();
                          referenceImages.forEach(({ file }) => formData.append("images", file));
                          const res = await fetch("/api/booking-reference-image", { method: "POST", body: formData });
                          if (!res.ok) throw new Error("Upload failed");
                          const data = await res.json();
                          const uploaded: string[] = data.urls ?? (data.url ? [data.url] : []);
                          setReferenceImageUrls(uploaded);
                        } catch {
                          toast({ title: "Image upload failed", description: "You can still continue — we'll note that no reference images were attached.", variant: "destructive" });
                        } finally {
                          setImageUploading(false);
                        }
                      }
                      setStep("notes");
                    }}
                    disabled={imageUploading}
                    className="btn-cta-glow bg-accent hover:bg-accent/80 active:scale-[0.97] active:bg-accent/70 inline-flex items-center justify-center gap-2 w-full sm:flex-1 px-8 py-4 rounded-full text-white uppercase tracking-widest text-xs font-bold font-subheading transition-all duration-300 disabled:opacity-60 disabled:cursor-wait"
                    data-testid="button-images-continue"
                    type="button"
                  >
                    {imageUploading ? <>Uploading... <span className="animate-spin inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full" /></> : <>Continue <ArrowRight size={14} /></>}
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {step === "notes" && (
            <motion.div key="notes" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="max-w-2xl mx-auto rounded-2xl p-8 md:p-10 bg-black/80 backdrop-blur-sm border border-white/10">
                <div className="flex items-start gap-4 mb-2">
                  <div className="flex items-center gap-3">
                    <MessageSquare size={20} className="text-accent" />
                    <h2 className="text-xl font-heading tracking-wide text-white">
                      {serviceCategory === "piercing" ? "Tell Us About Your Piercing" : "Tell Us About Your Design"}
                    </h2>
                  </div>
                </div>
                <p className="text-sm text-white/50 mb-6">
                  {serviceCategory === "piercing"
                    ? "Describe your piercing idea — placement, jewelry preference, and any relevant context. Even a rough idea is enough to get started. If we need more information we'll reach out to you."
                    : "Describe your design idea in as much detail as you can — even a rough idea is enough to get started. If we need more information we'll reach out to you."}
                </p>

                {serviceCategory !== "piercing" && referenceImages.length > 0 && (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-accent/5 border border-accent/15">
                    <ImageIcon size={14} className="text-accent shrink-0" />
                    <p className="text-xs text-white/50">{referenceImages.length} reference image{referenceImages.length !== 1 ? "s" : ""} uploaded — <button type="button" className="text-violet-300 hover:text-violet-200 underline underline-offset-2 transition-colors" onClick={() => setStep("images")}>view or change</button></p>
                  </div>
                )}

                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={5}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:border-accent focus:outline-none transition-colors resize-none mb-4"
                  placeholder={serviceCategory === "piercing"
                    ? "e.g. I'd like a helix piercing on my right ear — interested in a titanium hoop. I've had lobe piercings before. Open to your recommendations on gauge and jewelry style..."
                    : "e.g. I want a black and grey wolf howling at the moon, realistic style — inspired by [artist or image]. Open to your interpretation on the finer details..."}
                  data-testid="textarea-booking-notes"
                />

                {serviceCategory !== "piercing" && (
                  <div className="mb-4">
                    <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/50 font-subheading mb-2">
                      <Link2 size={12} className="text-accent/60" />
                      Inspiration links <span className="normal-case tracking-normal text-white/55">(optional — Pinterest, Instagram, etc.)</span>
                    </label>
                    <textarea
                      value={referenceLinks}
                      onChange={(e) => setReferenceLinks(e.target.value)}
                      rows={3}
                      className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:border-accent/50 focus:outline-none transition-colors resize-none text-sm font-mono"
                      placeholder={"https://www.pinterest.com/pin/...\nhttps://..."}
                      data-testid="textarea-inspiration-links"
                    />
                    {linkPreviews.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {linkPreviews.map((p, i) => (
                          <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-white/10 bg-black/40 flex items-center justify-center" data-testid={`img-link-preview-${i}`}>
                            {p.loading && <div className="w-4 h-4 border-2 border-accent/40 border-t-accent rounded-full animate-spin" />}
                            {!p.loading && p.error && <Link2 size={18} className="text-white/20" />}
                            {!p.loading && p.previewUrl && (
                              <img src={p.previewUrl} alt="" className="w-full h-full object-cover" onError={() => setLinkPreviews(prev => prev.map((x, idx) => idx === i ? { ...x, previewUrl: null, error: true } : x))} />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {(isAdmin || isWalkin) && (
                  <label className="flex items-center gap-3 cursor-pointer p-4 rounded-xl border border-blue-500/20 bg-blue-500/5" data-testid="label-send-client-email">
                    <input
                      type="checkbox"
                      checked={sendClientEmail}
                      onChange={(e) => setSendClientEmail(e.target.checked)}
                      className="w-4 h-4 rounded border-white/20 bg-black/50 text-accent focus:ring-accent/50 cursor-pointer accent-[#3b82f6]"
                      data-testid="checkbox-send-client-email"
                    />
                    <div>
                      <p className="text-sm font-semibold text-white">Send confirmation email to client</p>
                      <p className="text-xs text-white/60">Uncheck for data entry of appointments already communicated.</p>
                    </div>
                  </label>
                )}

                {isWalkin && (
                  <div className="space-y-1.5">
                    <label className="block text-xs uppercase tracking-widest text-muted-foreground font-subheading">
                      Request Deposit ($) <span className="normal-case text-muted-foreground/60">— optional</span>
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={walkinDepositAmount}
                      onChange={(e) => setWalkinDepositAmount(e.target.value)}
                      placeholder="0"
                      data-testid="input-walkin-deposit"
                      className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:border-accent focus:outline-none transition-colors"
                    />
                    <p className="text-xs text-muted-foreground/60">Enter a non-zero amount to request a deposit and send the client a payment email.</p>
                  </div>
                )}

                {isArtistPortal && (
                  <div className="p-4 rounded-xl border border-violet-500/20 bg-violet-500/5 space-y-3" data-testid="artist-portal-confirm-mode">
                    <p className="text-xs font-bold text-violet-300 uppercase tracking-wider">Client Email</p>
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="label-confirm-mode-confirmed">
                      <input
                        type="radio"
                        name="artistPortalConfirmMode"
                        value="confirmed"
                        checked={artistPortalConfirmMode === "confirmed"}
                        onChange={() => setArtistPortalConfirmMode("confirmed")}
                        className="w-4 h-4 accent-[#8b5cf6] cursor-pointer"
                        data-testid="radio-confirm-mode-confirmed"
                      />
                      <div>
                        <p className="text-sm font-semibold text-white">Send confirmation email</p>
                        <p className="text-xs text-white/60">Confirms the booking immediately and sends a calendar invite.</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="label-confirm-mode-pending">
                      <input
                        type="radio"
                        name="artistPortalConfirmMode"
                        value="pending"
                        checked={artistPortalConfirmMode === "pending"}
                        onChange={() => setArtistPortalConfirmMode("pending")}
                        className="w-4 h-4 accent-[#8b5cf6] cursor-pointer"
                        data-testid="radio-confirm-mode-pending"
                      />
                      <div>
                        <p className="text-sm font-semibold text-white">Ask client to confirm</p>
                        <p className="text-xs text-white/60">Sends a pending email with a "Confirm My Appointment" button.</p>
                      </div>
                    </label>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap items-center gap-3">
                  <span className="text-xs text-white/40 uppercase tracking-widest font-subheading shrink-0">Change:</span>
                  {isSoftBooking ? (
                    <button
                      onClick={() => setStep("availability")}
                      className="inline-flex items-center gap-1.5 bg-accent/10 border border-accent/20 text-violet-300 hover:bg-accent hover:text-white hover:border-accent transition-colors uppercase tracking-widest text-xs font-bold font-subheading px-4 py-2 rounded-full"
                      data-testid="button-notes-change-availability"
                      type="button"
                    >
                      <CalendarIcon size={11} /> Availability
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => { setReturnToStep("notes"); setStep("calendar"); }}
                        className="inline-flex items-center gap-1.5 bg-accent/10 border border-accent/20 text-violet-300 hover:bg-accent hover:text-white hover:border-accent transition-colors uppercase tracking-widest text-xs font-bold font-subheading px-4 py-2 rounded-full"
                        data-testid="button-notes-change-date"
                        type="button"
                      >
                        <Calendar size={11} /> {selectedDateFormatted || "Date"}
                      </button>
                      <button
                        onClick={() => { setReturnToStep("notes"); setStep("time"); }}
                        className="inline-flex items-center gap-1.5 bg-accent/10 border border-accent/20 text-violet-300 hover:bg-accent hover:text-white hover:border-accent transition-colors uppercase tracking-widest text-xs font-bold font-subheading px-4 py-2 rounded-full"
                        data-testid="button-notes-change-time"
                        type="button"
                      >
                        <Clock size={11} /> {selectedTime || "Time"}
                      </button>
                    </>
                  )}
                </div>

                {(() => {
                  const canProceedNotes = isArtistPortal || form.notes.trim().length > 0 || referenceImages.length > 0 || referenceLinks.trim().length > 0;
                  const handleNotesContinue = () => {
                    if (canProceedNotes) { setStep("form"); return; }
                    setNotesWiggle(false);
                    requestAnimationFrame(() => {
                      setNotesWiggle(true);
                      setNotesNotif(true);
                      if (notesWiggleTimer.current) clearTimeout(notesWiggleTimer.current);
                      notesWiggleTimer.current = setTimeout(() => setNotesWiggle(false), 450);
                      if (notesNotifTimer.current) clearTimeout(notesNotifTimer.current);
                      notesNotifTimer.current = setTimeout(() => setNotesNotif(false), 3500);
                    });
                  };
                  return (
                    <>
                      {notesNotif && (
                        <div className="flex items-center gap-2 mb-4 px-4 py-3 rounded-xl border border-accent/30 bg-accent/5 animate-shake" data-testid="notif-notes-gate">
                          <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse shrink-0" />
                          <p className="text-xs text-white/60">
                            Add a description to continue — the more detail you can provide, the better your artist can prepare for your session.
                          </p>
                        </div>
                      )}
                      <div className="flex flex-col sm:flex-row items-center gap-4">
                        <button
                          onClick={() => setStep(isStepVisible(serviceCategory, "images") ? "images" : "estimate")}
                          className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 rounded-full bg-accent/20 border border-accent/30 text-white uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent hover:border-accent active:scale-[0.97] active:brightness-90 transition-all group"
                          data-testid="button-notes-back"
                        >
                          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Back
                        </button>
                        <button
                          onClick={handleNotesContinue}
                          onAnimationEnd={() => setNotesWiggle(false)}
                          className={`${canProceedNotes ? "btn-cta-glow bg-accent hover:bg-accent/80 active:scale-[0.97] active:bg-accent/70" : "bg-white/10 text-white/30"}${notesWiggle ? " animate-shake" : ""} inline-flex items-center justify-center gap-2 w-full sm:flex-1 px-8 py-4 rounded-full text-white uppercase tracking-widest text-xs font-bold font-subheading transition-all duration-300`}
                          data-testid="button-notes-continue"
                        >
                          Continue <ArrowRight size={14} />
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </motion.div>
          )}

          {step === "duplicate-check" && duplicateBooking && (
            <motion.div key="duplicate-check" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
              <div className="rounded-2xl p-8 bg-black/80 backdrop-blur-sm border border-amber-500/30 max-w-lg mx-auto">
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4">
                    <AlertCircle size={32} className="text-amber-400" />
                  </div>
                  <h2 className="text-2xl font-heading text-white mb-2" data-testid="text-duplicate-check-title">
                    Heads up — we already have a request from you
                  </h2>
                  <p className="text-white/60 text-sm leading-relaxed">
                    There's an existing booking request for <span className="text-white font-medium">{duplicateBooking.artistName}</span> on this same date with your email address.
                  </p>
                </div>

                <div className="rounded-xl border border-white/10 bg-white/5 p-4 mb-6 text-left" data-testid="duplicate-booking-details">
                  <p className="text-xs text-white/60 uppercase tracking-widest font-subheading mb-3">Existing Request</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar size={14} className="text-accent shrink-0" />
                      <span className="text-white/80">{new Date(duplicateBooking.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock size={14} className="text-accent shrink-0" />
                      <span className="text-white/80">{formatTimePref(duplicateBooking.time)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <User size={14} className="text-accent shrink-0" />
                      <span className="text-white/80">{duplicateBooking.artistName}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Info size={14} className="text-white/60 shrink-0" />
                      <span className="text-white/60 font-mono text-xs">{duplicateBooking.referenceCode}</span>
                    </div>
                  </div>
                </div>

                <p className="text-white/60 text-sm text-center mb-5">
                  Is this a <strong className="text-white">separate session</strong> (e.g. a second sitting or different piece), or did you accidentally submit twice?
                </p>

                <div className="flex flex-col gap-3">
                  <button
                    data-testid="button-confirm-separate-session"
                    onClick={() => {
                      if (pendingSubmitRef.current) {
                        bookingMutation.mutate({ ...pendingSubmitRef.current, confirmDuplicate: true } as any);
                      }
                    }}
                    disabled={bookingMutation.isPending}
                    className="w-full py-3.5 rounded-full bg-accent text-black font-bold text-sm uppercase tracking-widest hover:bg-accent/90 transition-colors disabled:opacity-50"
                  >
                    {bookingMutation.isPending ? "Submitting…" : "It's a separate session — submit anyway"}
                  </button>
                  <button
                    data-testid="button-confirm-is-duplicate"
                    onClick={() => {
                      setDuplicateBooking(null);
                      setStep("notes");
                    }}
                    className="w-full py-3.5 rounded-full bg-white/5 border border-white/10 text-white/70 font-medium text-sm hover:bg-white/10 hover:text-white transition-colors"
                  >
                    It's a duplicate — don't submit again
                  </button>
                </div>
                <p className="text-center text-xs text-white/55 mt-4">
                  Not sure? The studio can merge duplicate requests — it's okay to submit either way.
                </p>
              </div>
            </motion.div>
          )}

          {step === "walkin-warnings" && walkinWarnings && (
            <motion.div key="walkin-warnings" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
              <div className="rounded-2xl p-8 bg-black/80 backdrop-blur-sm border border-amber-500/30 max-w-lg mx-auto">
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4">
                    <AlertCircle size={32} className="text-amber-400" />
                  </div>
                  <h2 className="text-2xl font-heading text-white mb-2" data-testid="text-walkin-warnings-title">
                    Review Before Confirming
                  </h2>
                </div>
                {walkinWarnings.duplicate && (
                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 mb-3 text-left" data-testid="walkin-warning-duplicate">
                    <p className="text-sm font-semibold text-amber-400 mb-1">Possible Duplicate Booking</p>
                    <p className="text-white/60 text-xs">{walkinWarnings.duplicate}</p>
                  </div>
                )}
                {walkinWarnings.overbooking && (
                  <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 mb-3 text-left" data-testid="walkin-warning-overbooking">
                    <p className="text-sm font-semibold text-red-400 mb-1">Artist May Be Overbooked</p>
                    <p className="text-white/60 text-xs">{walkinWarnings.overbooking}</p>
                  </div>
                )}
                <p className="text-white/50 text-sm text-center mb-5">Do you still want to proceed with this walk-in booking?</p>
                <div className="flex flex-col gap-3">
                  <button
                    data-testid="button-walkin-confirm-anyway"
                    onClick={() => {
                      if (pendingSubmitRef.current) {
                        bookingMutation.mutate({ ...pendingSubmitRef.current, acknowledge: true } as any);
                      }
                    }}
                    disabled={bookingMutation.isPending}
                    className="w-full py-3.5 rounded-full bg-accent text-white font-bold text-sm uppercase tracking-widest hover:bg-accent/90 transition-colors disabled:opacity-50"
                  >
                    {bookingMutation.isPending ? "Confirming…" : "Book Anyway"}
                  </button>
                  <button
                    data-testid="button-walkin-go-back"
                    onClick={() => { setWalkinWarnings(null); setStep("notes"); }}
                    className="w-full py-3.5 rounded-full bg-white/5 border border-white/10 text-white/70 font-medium text-sm hover:bg-white/10 hover:text-white transition-colors"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {step === "limit-reached" && limitInfo && (
            <motion.div key="limit-reached" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
              <div className="rounded-2xl p-10 text-center bg-black/80 backdrop-blur-sm border border-amber-500/30" data-testid="booking-limit-reached">
                <div className="w-16 h-16 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto mb-6">
                  <span style={{ fontSize: 32 }}>⏳</span>
                </div>
                <h2 className="text-2xl font-heading text-white mb-3" data-testid="text-limit-reached-heading">
                  Daily Request Limit Reached
                </h2>
                <p className="text-amber-300/80 text-sm mb-2 font-medium">
                  You've submitted {limitInfo.submitted} of {limitInfo.limit} booking requests allowed per day.
                </p>
                <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-md mx-auto">
                  To keep our calendar fair for everyone, we limit online booking requests to {limitInfo.limit} per day. Your limit resets at midnight — come back tomorrow to book more.
                </p>
                <div className="rounded-xl bg-white/5 border border-white/10 p-5 mb-6 text-left max-w-sm mx-auto" data-testid="limit-studio-contact">
                  <p className="text-xs text-white/60 uppercase tracking-widest font-semibold mb-3">Need to book more? Contact {limitInfo.studioName} directly</p>
                  {limitInfo.studioPhone && (
                    <a href={`tel:${limitInfo.studioPhone}`} className="flex items-center gap-2 text-sm text-violet-300 hover:text-violet-200 transition-colors mb-2" data-testid="link-limit-phone">
                      <Phone size={14} className="shrink-0" />
                      {limitInfo.studioPhone}
                    </a>
                  )}
                  {limitInfo.studioEmail && (
                    <a href={`mailto:${limitInfo.studioEmail}`} className="flex items-center gap-2 text-sm text-violet-300 hover:text-violet-200 transition-colors" data-testid="link-limit-email" // email-exposure-ok: booking-limit gate — user must email studio to arrange availability
                    >
                      <span style={{ fontSize: 14 }}>✉️</span>
                      {limitInfo.studioEmail}
                    </a>
                  )}
                  {!limitInfo.studioPhone && !limitInfo.studioEmail && (
                    <p className="text-sm text-white/50">Contact the studio directly and they can add your appointment manually.</p>
                  )}
                </div>
                <p className="text-white/55 text-xs">
                  Alternatively, the studio can add your appointment manually — just reach out and let them know.
                </p>
              </div>
            </motion.div>
          )}

          {step === "done" && (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
              <div className="rounded-2xl p-12 text-center bg-black/80 backdrop-blur-sm border border-white/10">
                <CheckCircle size={64} className="text-accent mx-auto mb-6" />
                <h2 className="text-3xl font-heading text-white mb-4" data-testid="text-booking-done">
                  {isAdmin ? "Appointment Added!" : isWalkin ? "Walk-In Booked!" : "Request Submitted!"}
                </h2>
                <p className="text-muted-foreground font-light mb-2 text-lg leading-relaxed max-w-lg mx-auto">
                  {isAdmin || isWalkin
                    ? <><span className="text-accent">{selectedArtistData?.name}</span> — <span className="text-white">{selectedDateFormatted}</span> at {formatTimePref(selectedTime)}. Booking confirmed.</>
                    : isSoftBooking
                      ? <>Your appointment request with <span className="text-accent">{selectedArtistData?.name}</span> has been submitted. We'll be in touch to confirm a time.</>
                      : <>Your appointment request with <span className="text-accent">{selectedArtistData?.name}</span> on{" "}<span className="text-white">{selectedDateFormatted}</span> ({formatTimePref(selectedTime)}) has been submitted.</>
                  }
                </p>
                {isPublic && (
                  <div className="inline-flex items-center gap-2 text-sm text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-2 mb-4">
                    <Phone size={14} className="shrink-0" />
                    <span>{t("welcome.contact_note")}</span>
                  </div>
                )}

                {(submittedReferenceImageSignedUrls.length > 0 || referenceImageUrls.length > 0) && (
                  <div className="rounded-xl border border-white/10 bg-white/5 p-4 mb-4 max-w-md mx-auto text-left" data-testid="confirmation-reference-images">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle size={14} className="text-green-400 shrink-0" />
                      <p className="text-xs font-semibold text-green-300 uppercase tracking-widest">
                        {(submittedReferenceImageSignedUrls.length || referenceImageUrls.length)} Inspiration Image{(submittedReferenceImageSignedUrls.length || referenceImageUrls.length) !== 1 ? "s" : ""} Saved
                      </p>
                    </div>
                    {submittedReferenceImageSignedUrls.length > 0 && (
                      <div className={`grid gap-2 ${submittedReferenceImageSignedUrls.length === 1 ? "grid-cols-1" : "grid-cols-3"} mb-2`}>
                        {submittedReferenceImageSignedUrls.slice(0, 6).map((src, idx) => (
                          <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-white/10 bg-black/40">
                            <img
                              src={src}
                              alt={`Saved reference ${idx + 1}`}
                              className="w-full h-full object-cover"
                              data-testid={`img-confirmation-reference-${idx}`}
                            />
                          </div>
                        ))}
                        {submittedReferenceImageSignedUrls.length > 6 && (
                          <div className="relative aspect-square rounded-lg border border-white/10 bg-black/40 flex items-center justify-center">
                            <span className="text-xs font-semibold text-white/50">+{submittedReferenceImageSignedUrls.length - 6} more</span>
                          </div>
                        )}
                      </div>
                    )}
                    {referenceImages.length === 0 && referenceImageUrls.length > 0 && (
                      <p className="text-[10px] text-violet-300 mt-1" data-testid="text-inspiration-link-count">
                        {referenceImageUrls.length} image{referenceImageUrls.length !== 1 ? "s" : ""} extracted from your inspiration links
                      </p>
                    )}
                    <p className="text-[10px] text-white/55 mt-2">Your artist will receive these with your booking.</p>
                  </div>
                )}

                {submittedReleaseFormOnFile && (
                  <div className="flex items-center justify-center gap-2 text-sm text-green-300/90 bg-green-500/10 border border-green-500/20 rounded-lg px-4 py-3 mb-4 max-w-md mx-auto" data-testid="release-form-on-file-notice">
                    <CheckCircle size={16} className="shrink-0 text-green-400" />
                    <span>Your release form is already on file — no action needed.</span>
                  </div>
                )}

                <FeatureFlagZone flagKey="release_forms_enabled">
                {!isFlagEnabled("release_forms_enabled") && isPublic && !submittedReleaseFormOnFile && (
                  <div className="flex items-center justify-center gap-2 text-sm text-white/60 bg-white/5 border border-white/10 rounded-lg px-4 py-3 mb-4 max-w-md mx-auto" data-testid="release-form-shop-notice">
                    <ClipboardList size={14} className="shrink-0 text-white/40" />
                    <span>You may be asked to sign a release form at the shop before your appointment.</span>
                  </div>
                )}

                {submittedReleaseFormToken && !submittedReleaseFormOnFile && isFlagEnabled("release_forms_enabled") && (
                  <div className="rounded-xl border border-purple-500/30 bg-purple-500/8 p-5 mb-4 max-w-md mx-auto text-left" data-testid="release-form-required-notice">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0 mt-0.5">
                        <ClipboardList size={15} className="text-purple-300" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-purple-200 mb-1">Release Form Required</p>
                        <p className="text-xs text-white/60 leading-relaxed">
                          A signed release form is required before your appointment. You can complete it now or use the link we'll include in your confirmation email.
                        </p>
                      </div>
                    </div>
                    <a
                      href={`/release/${submittedReleaseFormToken}`}
                      className="btn-cta-glow inline-flex items-center justify-center gap-2 w-full py-3 rounded-full bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold uppercase tracking-widest transition-colors"
                      data-testid="button-complete-release-form"
                    >
                      Complete Release Form Now
                    </a>
                    <p className="text-[10px] text-white/55 text-center mt-2">You can also complete this later using the link in your confirmation email.</p>
                  </div>
                )}
                </FeatureFlagZone>

                {isPublic && submittedDepositRequired && (
                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/8 p-5 mb-4 max-w-md mx-auto text-left" data-testid="deposit-pending-panel">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
                        <DollarSign size={15} className="text-amber-300" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-amber-200 mb-1">
                          {submittedDepositAmount ? `$${submittedDepositAmount} Deposit Required to Confirm Your Spot` : "Deposit Required to Confirm Your Spot"}
                        </p>
                        <p className="text-xs text-white/60 leading-relaxed">
                          Your appointment is held for <strong className="text-white/80">48 hours</strong>. Pay your deposit to lock it in — you'll receive a confirmation email once it's received.
                        </p>
                      </div>
                    </div>
                    {submittedPaymentLink && (
                      <a
                        href={submittedPaymentLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold uppercase tracking-widest transition-colors mb-3"
                        data-testid="button-pay-deposit-now"
                      >
                        <DollarSign size={14} />
                        Pay Deposit Now
                      </a>
                    )}
                    <div className="flex items-center gap-2 text-xs text-white/50 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
                      <Phone size={12} className="shrink-0 text-white/30" />
                      <span>Prefer to pay in person? Just bring cash or card to the shop — deposit can be taken at your appointment.</span>
                    </div>
                    <p className="text-[10px] text-white/55 text-center mt-2">We'll also send you an email with payment options within a few minutes.</p>
                  </div>
                )}
                {isPublic && !submittedDepositRequired && (
                  <div className="inline-flex items-center gap-2 text-sm text-green-300/80 bg-green-500/10 border border-green-500/20 rounded-lg px-4 py-2 mb-4">
                    <DollarSign size={14} className="shrink-0" />
                    <span>All payment takes place at the shop — we accept cash and card.</span>
                  </div>
                )}
                {/* Variant D: post-submission inline estimate prompt — tattoo only */}
                {estimateVariant === "D" && isPublic && !showVariantDPostEstimate && isStepVisible(serviceCategory, "estimate") && (
                  <div className="mt-6 border-t border-white/10 pt-6">
                    <div className="rounded-xl border border-white/10 bg-black/60 p-5 max-w-md mx-auto text-left">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: "rgba(139,92,246,0.2)" }}>
                          <DollarSign size={15} style={{ color: "#8b5cf6" }} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white mb-1">Get a quick price estimate</p>
                          <p className="text-xs text-white/60 leading-relaxed">
                            Curious what your tattoo might cost? Answer 4 quick questions to get a ballpark — for personal planning only.
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => {
                            trackSubFunnelEvent("estimate-d-entry");
                            setShowVariantDPostEstimate(true);
                          }}
                          className="flex-1 py-2.5 rounded-full text-white text-xs font-bold uppercase tracking-widest transition-all"
                          style={{ backgroundColor: "#8b5cf6" }}
                          data-testid="button-est-d-open"
                        >
                          Yes, show me
                        </button>
                        <button
                          onClick={() => {
                            trackSubFunnelEvent("estimate-prompt:skipped");
                          }}
                          className="flex-1 py-2.5 rounded-full border border-white/10 text-white/50 hover:text-white/80 text-xs font-bold uppercase tracking-widest transition-all"
                          data-testid="button-est-d-dismiss"
                        >
                          No thanks
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {estimateVariant === "D" && showVariantDPostEstimate && variantDSubmittedBookingId && variantDSelfEstimateToken && (
                  <div className="mt-6 border-t border-white/10 pt-6">
                    <VariantDPostEstimate
                      bookingId={variantDSubmittedBookingId}
                      selfEstimateToken={variantDSelfEstimateToken}
                      onDone={() => setShowVariantDPostEstimate(false)}
                    />
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
                  <button
                    onClick={reset}
                    className="bg-accent text-white px-8 py-4 rounded-full uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 active:scale-[0.97] active:bg-accent/70 transition-all duration-300 shadow-[0_0_15px_rgba(139,92,246,0.4)] hover:shadow-[0_0_25px_rgba(139,92,246,0.6)]"
                    data-testid="button-book-another"
                  >
                    {isAdmin ? "Add Another Appointment" : isWalkin ? "New Walk-In" : "Book Another Appointment"}
                  </button>
                  {isPublic && (
                    <Link
                      href="/"
                      className="bg-accent text-white px-8 py-4 rounded-full uppercase tracking-widest text-xs font-bold font-subheading hover:bg-accent/80 active:scale-[0.97] active:bg-accent/70 transition-all duration-300 shadow-[0_0_20px_rgba(139,92,246,0.4)] hover:shadow-[0_0_30px_rgba(139,92,246,0.6)]"
                      data-testid="button-return-home"
                    >
                      Return to Studio
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>}
        </div>
      </div>

      {/* Waitlist Modal */}
      {showWaitlistForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4" // @modal-overflow-ok waitlist join form: max-w-lg with overflow-hidden on card — self-bounded
          style={{ background: "rgba(0,0,0,0.85)" }}
          data-testid="waitlist-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) { setShowWaitlistForm(false); setWaitlistStatus(null); setWaitlistTargetArtistSlug(null); } }}
        >
          <div
            className="w-full max-w-lg bg-[#111] border border-white/15 rounded-2xl overflow-hidden shadow-2xl"
            data-testid="waitlist-modal"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <Bell size={18} className="text-accent" />
                <div>
                  <p className="text-sm font-bold text-white font-subheading">Join the Waitlist</p>
                  <p className="text-xs text-white/50">Get notified when {artists.find(a => a.slug === waitlistTargetArtistSlug)?.name ?? selectedArtistData?.name} has an opening</p>
                </div>
              </div>
              <button
                onClick={() => { setShowWaitlistForm(false); setWaitlistStatus(null); setWaitlistTargetArtistSlug(null); }}
                className="text-white/40 hover:text-white transition-colors p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
                data-testid="button-close-waitlist-modal"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-white/50 mb-1 uppercase tracking-wider font-subheading">Name *</label>
                  <input
                    type="text"
                    value={waitlistForm.name}
                    onChange={(e) => setWaitlistForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Your full name"
                    className="w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-accent/60"
                    data-testid="input-waitlist-name"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1 uppercase tracking-wider font-subheading">Email *</label>
                  <input
                    type="email"
                    value={waitlistForm.email}
                    onChange={(e) => { setWaitlistEmailTouched(true); setWaitlistForm((f) => ({ ...f, email: e.target.value })); }}
                    onBlur={() => setWaitlistEmailTouched(true)}
                    placeholder="you@email.com"
                    className={`w-full bg-black/60 border rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/50 focus:outline-none ${waitlistEmailTouched && waitlistForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(waitlistForm.email) ? "border-red-500/60 focus:border-red-500/80" : "border-white/15 focus:border-accent/60"}`}
                    data-testid="input-waitlist-email"
                  />
                  {waitlistEmailTouched && waitlistForm.email && !waitlistForm.email.includes("@") && (
                    <p className="mt-1 text-xs text-red-400 flex items-center gap-1" data-testid="text-waitlist-email-missing-at">
                      <span>⚠</span> An email address must include an '@' — e.g. you@example.com
                    </p>
                  )}
                  {waitlistEmailTouched && waitlistForm.email && waitlistForm.email.includes("@") && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(waitlistForm.email) && (
                    <p className="mt-1 text-xs text-amber-400/90 flex items-center gap-1" data-testid="text-waitlist-email-invalid">
                      <span>⚠</span> Please enter a complete email address — e.g. you@example.com
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1 uppercase tracking-wider font-subheading">Phone <span className="text-white/55 normal-case">(optional)</span></label>
                  <input
                    type="tel"
                    value={waitlistForm.phone}
                    onChange={(e) => setWaitlistForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="555-123-4567"
                    className="w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-accent/60"
                    data-testid="input-waitlist-phone"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1 uppercase tracking-wider font-subheading">Service <span className="text-white/55 normal-case">(optional)</span></label>
                  <select
                    value={waitlistForm.serviceType}
                    onChange={(e) => setWaitlistForm((f) => ({ ...f, serviceType: e.target.value }))}
                    className="w-full border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-accent/60 appearance-none"
                    style={{ background: "#111111", colorScheme: "dark" }}
                    data-testid="select-waitlist-service"
                  >
                    <option value="" style={{ background: "#111111", color: "#fff" }}>Any service</option>
                    {SERVICE_TYPES.map((s) => <option key={s} value={s} style={{ background: "#111111", color: "#fff" }}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1 uppercase tracking-wider font-subheading">Preferred Month <span className="text-white/55 normal-case">(optional)</span></label>
                  <input
                    type="month"
                    value={waitlistForm.preferredMonth}
                    onChange={(e) => setWaitlistForm((f) => ({ ...f, preferredMonth: e.target.value }))}
                    className="w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-accent/60"
                    style={{ colorScheme: "dark" }}
                    data-testid="input-waitlist-preferred-month"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1 uppercase tracking-wider font-subheading">Notes <span className="text-white/55 normal-case">(optional)</span></label>
                <textarea
                  value={waitlistForm.notes}
                  onChange={(e) => setWaitlistForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Any details about your idea, preferred timeframe, etc."
                  rows={2}
                  className="w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-accent/60 resize-none"
                  data-testid="textarea-waitlist-notes"
                />
              </div>
              {waitlistStatus === "error" && (
                <p className="text-xs text-red-400">Something went wrong. Please try again.</p>
              )}
            </div>
            <div className="px-6 pb-5 flex justify-end gap-3">
              <button
                onClick={() => { setShowWaitlistForm(false); setWaitlistStatus(null); setWaitlistTargetArtistSlug(null); }}
                className="px-5 py-2.5 rounded-full text-xs font-subheading font-bold uppercase tracking-widest border border-white/15 text-white/60 hover:text-white hover:border-white/30 transition-all"
                data-testid="button-cancel-waitlist"
              >
                Cancel
              </button>
              <button
                disabled={!waitlistForm.name.trim() || !waitlistForm.email.trim() || waitlistStatus === "submitting"}
                onClick={async () => {
                  if (!waitlistForm.name.trim() || !waitlistForm.email.trim()) return;
                  setWaitlistStatus("submitting");
                  try {
                    const res = await fetch("/api/waitlist", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        artistSlug: waitlistTargetArtistSlug ?? selectedArtist,
                        clientName: waitlistForm.name.trim(),
                        clientEmail: waitlistForm.email.trim(),
                        clientPhone: waitlistForm.phone.trim() || undefined,
                        serviceType: waitlistForm.serviceType || undefined,
                        notes: waitlistForm.notes.trim() || undefined,
                        requestedDate: waitlistForm.preferredMonth || undefined,
                      }),
                    });
                    const data = await res.json();
                    if (res.ok && data.success) {
                      setWaitlistStatus(data.alreadyOnList ? "alreadyOnList" : "done");
                      setShowWaitlistForm(false);
                      setWaitlistTargetArtistSlug(null);
                    } else {
                      setWaitlistStatus("error");
                    }
                  } catch (_swallowedErr) {
                    captureException(_swallowedErr);
                    setWaitlistStatus("error");
                  }
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-widest font-subheading font-bold bg-accent text-white hover:bg-accent/80 active:scale-[0.97] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="button-submit-waitlist"
              >
                {waitlistStatus === "submitting" ? (
                  <><span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Joining...</>
                ) : (
                  <><Bell size={13} /> Join Waitlist</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Dev widget: step jumper + split-test switcher ── */}
      {/* Activated in dev env automatically, or in prod via ?devtools=1 / localStorage */}
      {showDevWidget && (
        <>
          {/* Collapsed trigger — matches app pill-button style */}
          <button
            onClick={() => setDevWidgetOpen(o => !o)}
            data-testid="dev-widget-trigger"
            className="fixed bottom-4 right-4 z-[200] inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-black/90 border border-yellow-400/25 text-yellow-400/55 hover:text-yellow-400 hover:border-yellow-400/50 text-[10px] font-bold uppercase tracking-widest transition-all shadow-lg shadow-black/60 select-none min-h-[44px] backdrop-blur-sm"
            title={devWidgetOpen ? "Close Dev Tools" : "Open Dev Tools"}
          >
            <span className="text-[12px] leading-none">⚙</span>
            <span>Dev</span>
            {devVariantOverride && (
              <span
                className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[8px] font-black leading-none"
                style={{ background: "rgba(250,204,21,0.25)", color: "#fde047" }}
              >
                {devVariantOverride}
              </span>
            )}
          </button>

          {/* Expanded panel — floats above the trigger */}
          {devWidgetOpen && (
            <div
              className="fixed bottom-16 right-4 z-[200] w-56 rounded-2xl overflow-hidden shadow-2xl shadow-black/80 border border-white/10"
              data-testid="dev-step-widget"
              style={{ background: "rgba(5,5,5,0.97)" }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/8">
                <span className="text-[9px] text-yellow-400/50 font-bold uppercase tracking-[0.2em] select-none">Dev Tools</span>
                <button
                  onClick={() => setDevWidgetOpen(false)}
                  className="text-white/25 hover:text-white/60 transition-colors text-sm leading-none w-5 h-5 flex items-center justify-center"
                >×</button>
              </div>

              {/* Variant picker */}
              <div className="px-3 py-3 border-b border-white/8">
                <div className="text-[8px] text-yellow-400/40 uppercase tracking-widest mb-2 select-none">Estimate Variant</div>
                <div className="flex gap-1.5">
                  {(["A","B","C","D"] as EstimateVariant[]).map(v => {
                    const isActive = estimateVariant === v;
                    const isOverride = devVariantOverride === v;
                    return (
                      <button
                        key={v}
                        data-testid={`dev-variant-${v}`}
                        onClick={() => {
                          localStorage.setItem("booking_estimate_variant", v);
                          setDevVariantOverride(v);
                          setEstimateVariant(v);
                          setEstSubStep(0);
                          setEstShowResults(false);
                          setEstSize(null); setEstComplexity(null); setEstPlacement(null); setEstColor(null);
                          setVariantCBucket(null);
                          setStep("estimate");
                          setBookingStarted(true);
                        }}
                        className="flex-1 text-center text-[11px] font-bold font-mono py-2.5 rounded-lg transition-all min-h-[44px]"
                        style={{
                          background: isActive ? "rgba(250,204,21,0.18)" : "rgba(255,255,255,0.04)",
                          color: isActive ? "#fde047" : "rgba(255,255,255,0.25)",
                          border: isOverride
                            ? "1px solid rgba(250,204,21,0.55)"
                            : isActive
                            ? "1px solid rgba(250,204,21,0.2)"
                            : "1px solid rgba(255,255,255,0.07)",
                        }}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[8px] text-white/20 select-none">
                    {devVariantOverride ? `pinned → ${devVariantOverride}` : "auto-assigned"}
                  </span>
                  {devVariantOverride && (
                    <button
                      data-testid="dev-variant-reset"
                      onClick={() => {
                        localStorage.removeItem("booking_estimate_variant");
                        setDevVariantOverride(null);
                      }}
                      className="text-[8px] text-white/25 hover:text-white/55 transition-colors"
                    >
                      ✕ clear
                    </button>
                  )}
                </div>
              </div>

              {/* Step jumper */}
              <div className="px-3 py-3 border-b border-white/8">
                <div className="text-[8px] text-yellow-400/40 uppercase tracking-widest mb-2 select-none">Jump to Step</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {(["service-type","artist","calendar","time","estimate","body-map","notes","form","done"] as const).map(s => (
                    <button
                      key={s}
                      onClick={() => { setBookingStarted(true); setStep(s as typeof step); }}
                      data-testid={`dev-goto-${s}`}
                      className="text-left text-[9px] font-mono px-2 py-2 rounded-lg transition-all min-h-[44px]"
                      style={{
                        background: step === s ? "rgba(250,204,21,0.12)" : "rgba(255,255,255,0.03)",
                        color: step === s ? "#fde047" : "rgba(255,255,255,0.25)",
                        border: step === s ? "1px solid rgba(250,204,21,0.2)" : "1px solid rgba(255,255,255,0.06)",
                      }}
                    >
                      {step === s ? "▶ " : ""}{s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Close devtools */}
              <button
                data-testid="dev-widget-close"
                onClick={() => {
                  localStorage.removeItem("booking_devtools");
                  localStorage.removeItem("booking_estimate_variant");
                  window.location.search = "";
                }}
                className="w-full text-[8px] text-white/18 hover:text-white/45 text-center py-2.5 transition-colors select-none"
              >
                ✕ close devtools
              </button>
            </div>
          )}
        </>
      )}

      {isPublic && step !== "done" && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50"
          data-testid="banner-not-submitted"
          style={{
            opacity: footerVisible ? 0 : 1,
            transform: footerVisible ? "translateY(100%)" : "translateY(0)",
            transition: "opacity 0.25s ease, transform 0.25s ease",
            pointerEvents: footerVisible ? "none" : "auto",
          }}
        >
          {/* Glowing accent line */}
          <div className="h-px bg-gradient-to-r from-transparent via-amber-400/70 to-transparent" />
          <div
            className="backdrop-blur-md"
            style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.97) 0%, rgba(30,16,0,0.97) 50%, rgba(0,0,0,0.97) 100%)" }}
          >
            <div className="container mx-auto px-5 max-w-5xl py-3">
              <div className="flex items-center gap-4">

                {/* Step counter */}
                <div className="shrink-0 text-left">
                  <p className="text-[10px] font-bold text-amber-400/90 uppercase tracking-[0.15em] font-subheading leading-none" data-testid="booking-step-counter">
                    Step {currentMainStepIndex >= 0 ? currentMainStepIndex + 1 : 1}
                    <span className="text-amber-400/40"> / {mainSteps.length}</span>
                  </p>
                  <p className="text-[9px] text-amber-300/40 uppercase tracking-widest mt-0.5 leading-none hidden sm:block">
                    {mainStepLabels[step] ?? step}
                  </p>
                </div>

                {/* Progress bar */}
                <div className="flex-1 flex flex-col gap-1">
                  <div className="h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.07)" }}>
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${Math.max(4, ((currentMainStepIndex >= 0 ? currentMainStepIndex + 1 : 1) / mainSteps.length) * 100)}%`,
                        background: "linear-gradient(90deg, #f59e0b, #fcd34d)",
                        boxShadow: "0 0 8px rgba(251,191,36,0.5)",
                      }}
                    />
                  </div>
                  {/* Step dots */}
                  <div className="flex items-center justify-between px-px">
                    {mainSteps.map((_, i) => (
                      <span
                        key={i}
                        className="w-0.5 h-0.5 rounded-full transition-colors duration-500"
                        style={{ background: i <= (currentMainStepIndex >= 0 ? currentMainStepIndex : 0) ? "rgba(251,191,36,0.6)" : "rgba(255,255,255,0.12)" }}
                      />
                    ))}
                  </div>
                </div>

                {/* Status pill */}
                <div className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.18)" }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse shrink-0" />
                  <span className="text-[9px] font-bold text-amber-300/80 uppercase tracking-widest whitespace-nowrap hidden sm:inline">Not submitted</span>
                  <span className="text-[9px] font-bold text-amber-300/80 uppercase tracking-widest whitespace-nowrap sm:hidden">Pending</span>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Skip reference images confirmation ── */}
      <AnimatePresence>
        {showImagesSkipConfirm && (
          <motion.div
            key="images-skip-confirm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[200] overflow-y-auto"
            style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
          >
            <div className="flex min-h-full items-center justify-center px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.93, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 12 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-sm rounded-2xl bg-neutral-900 border border-white/10 p-6 space-y-4"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0 mt-0.5">
                  <AlertCircle size={18} className="text-amber-400" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">No reference images uploaded</p>
                  <p className="text-sm text-white/60 mt-1">
                    For any custom work, you'll need to provide a detailed description on the next step so your artist can understand your vision. The more specific, the better.
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2 pt-1">
                <button
                  onClick={() => { setShowImagesSkipConfirm(false); setStep("notes"); }}
                  className="w-full py-3 rounded-full bg-accent text-white font-bold text-xs uppercase tracking-widest hover:bg-accent/80 active:scale-[0.97] transition-all"
                  data-testid="button-images-skip-confirm"
                >
                  Continue — I'll add a detailed description
                </button>
                <button
                  onClick={() => setShowImagesSkipConfirm(false)}
                  className="w-full py-3 rounded-full bg-white/5 border border-white/10 text-white/70 text-xs font-medium hover:bg-white/10 hover:text-white active:scale-[0.97] transition-all"
                  data-testid="button-images-skip-cancel"
                >
                  Go back &amp; add images
                </button>
              </div>
            </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Leave-page guard modal ── */}
      <AnimatePresence>
        {showLeaveModal && (
          <motion.div
            key="leave-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[200] flex items-center justify-center px-6" // @modal-overflow-ok leave booking confirm: max-w-sm, 1 heading + 1 paragraph + 2 buttons — bounded
            style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.93, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.93, y: 12 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-sm rounded-3xl p-8 bg-gray-950 border border-white/10 shadow-2xl"
            >
              <h3 className="text-white text-xl font-heading tracking-wide mb-2">Leave your booking?</h3>
              <p className="text-white/60 text-sm leading-relaxed mb-6">
                You've made progress — your draft is saved for 7 days so you can pick up right where you left off.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  data-testid="button-leave-modal-stay"
                  onClick={() => setShowLeaveModal(false)}
                  className="w-full px-6 py-3.5 rounded-full bg-accent text-white font-subheading text-xs uppercase tracking-widest hover:brightness-110 active:scale-[0.97] transition-all"
                >
                  Stay &amp; continue
                </button>
                <button
                  data-testid="button-leave-modal-leave"
                  onClick={() => {
                    setShowLeaveModal(false);
                    pendingNavRef.current?.();
                    pendingNavRef.current = null;
                  }}
                  className="w-full px-6 py-3.5 rounded-full bg-white/5 border border-white/10 text-white/60 font-subheading text-xs uppercase tracking-widest hover:bg-white/10 hover:text-white active:scale-[0.97] transition-all"
                >
                  Leave anyway
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Exit-intent modal — portalled to body to escape stacking context ── */}
      {createPortal(
        <AnimatePresence>
          {showExitIntent && (
            <motion.div
              key="exit-intent-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[9998] flex items-end sm:items-center justify-center p-4" // @modal-overflow-ok exit intent card: max-w-sm, short persuasive copy + 2 buttons — bounded
              style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
              onClick={(e) => { if (e.target === e.currentTarget) closeExitIntent(); }}
            >
              <motion.div
                key="exit-intent-card"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 24 }}
                transition={{ duration: 0.25 }}
                className="relative w-full max-w-sm rounded-2xl shadow-2xl p-6"
                style={{ background: "rgba(15,10,30,0.98)", border: "1px solid rgba(139,92,246,0.45)" }}
              >
                <button
                  data-testid="button-exit-intent-dismiss"
                  onClick={() => closeExitIntent()}
                  className="absolute top-3 right-3 min-w-[36px] min-h-[36px] rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all"
                  aria-label="Dismiss"
                >
                  <X size={14} />
                </button>

                <div className="mb-4">
                      <p className="font-subheading text-sm tracking-wide mb-1" style={{ color: "#a78bfa" }}>Don't lose your spot</p>
                      <p className="text-base font-semibold text-white mb-1">Your draft is saved.</p>
                      <p className="text-sm leading-relaxed" style={{ color: "#999" }}>
                        Enter your email and we'll send you a link to pick up right where you left off.
                      </p>
                    </div>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (!exitEmail || exitEmailStatus === "submitting") return;
                        setExitEmailStatus("submitting");
                        fetch("/api/booking-abandonment", {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            sessionId: funnelSessionIdRef.current,
                            email: exitEmail.trim(),
                            artistSlug: selectedArtist || null,
                            lastStep: step,
                          }),
                        })
                          .finally(() => closeExitIntent());
                      }}
                      className="flex flex-col gap-3"
                    >
                      <input
                        data-testid="input-exit-email"
                        type="email"
                        value={exitEmail}
                        onChange={(e) => { setExitEmailTouched(true); setExitEmail(e.target.value); }}
                        onBlur={() => setExitEmailTouched(true)}
                        placeholder="your@email.com"
                        required
                        className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder:text-white/50 outline-none focus:ring-2"
                        style={{ background: "rgba(255,255,255,0.06)", border: exitEmailTouched && exitEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(exitEmail) ? "1px solid rgba(239,68,68,0.6)" : "1px solid rgba(255,255,255,0.12)" }}
                      />
                      {exitEmailTouched && exitEmail && !exitEmail.includes("@") && (
                        <p className="text-xs text-red-400 flex items-center gap-1" data-testid="text-exit-email-missing-at">
                          <span>⚠</span> Include an '@' — e.g. you@example.com
                        </p>
                      )}
                      {exitEmailTouched && exitEmail && exitEmail.includes("@") && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(exitEmail) && (
                        <p className="text-xs text-amber-400/90 flex items-center gap-1" data-testid="text-exit-email-invalid">
                          <span>⚠</span> Complete email needed — e.g. you@example.com
                        </p>
                      )}
                      <button
                        data-testid="button-exit-save-spot"
                        type="submit"
                        disabled={exitEmailStatus === "submitting" || !exitEmail}
                        className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-50"
                        style={{ background: "#8b5cf6" }}
                      >
                        {exitEmailStatus === "submitting" ? "Saving…" : "Save my spot"}
                      </button>
                      <button
                        data-testid="button-exit-intent-no-thanks"
                        type="button"
                        onClick={() => closeExitIntent()}
                        className="text-xs text-center transition-colors"
                        style={{ color: "#555" }}
                      >
                        No thanks, I'll come back on my own
                      </button>
                    </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
